import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  useTheme,
  alpha,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  Chip,
  Paper,
  LinearProgress,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Factory as FactoryIcon,
  Assignment as AssignmentIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Add as AddIcon,
  MenuBook as MenuBookIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon,
  LocalDrink as LocalDrinkIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';

// Date utility functions to avoid timezone issues
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse as local date to avoid timezone shifts
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

// Parse date string as local date for comparisons
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  try {
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
  } catch (e) {
    return null;
  }
};

const ProductionLineOverview = forwardRef(({
  lineProduction,
  products,
  showSnackbar,
  isCreatingNew,
  userCompanyId,
  onUpdate,
  onBackToList,
  user
}, ref) => {
  const theme = useTheme();
  const { canViewLineOverview, canEditLineOverview } = usePermissions();
  
  const [lineProductionTimeFormat, setLineProductionTimeFormat] = useState('24hour');
  const [userProfile, setUserProfile] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [validationError, setValidationError] = useState('');
  const [locations, setLocations] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantInputValues, setVariantInputValues] = useState({});

  // Dialog states for product and location selection
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [dialogSelectedProduct, setDialogSelectedProduct] = useState(null);
  const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [unitConfigSelectionDialogOpen, setUnitConfigSelectionDialogOpen] = useState(false);
  const [unitConfigSearchTerm, setUnitConfigSearchTerm] = useState('');
  
  // Template selection states
  const [templates, setTemplates] = useState([]);
  const [templateSelectionDialogOpen, setTemplateSelectionDialogOpen] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateConsumption, setTemplateConsumption] = useState({
    caps: [],
    bottles: [],
    labels: [],
    supplies: []
  });

  // Batch selection states
  const [batches, setBatches] = useState([]);
  const [batchSelectionDialogOpen, setBatchSelectionDialogOpen] = useState(false);
  const [batchSearchTerm, setBatchSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Inventory states
  const [bottlesInventory, setBottlesInventory] = useState([]);
  const [capsInventory, setCapsInventory] = useState([]);
  const [labelsInventory, setLabelsInventory] = useState([]);
  const [suppliesInventory, setSuppliesInventory] = useState([]);
  const [inventoryAnalysis, setInventoryAnalysis] = useState(null);

  // Unit Configuration states
  const [unitConfigurations, setUnitConfigurations] = useState([]);
  const [selectedUnitConfig, setSelectedUnitConfig] = useState(null);
  const [unitInputs, setUnitInputs] = useState({});

  // Unit Configuration Creation states
  const [unitConfigDialogOpen, setUnitConfigDialogOpen] = useState(false);
  const [editingUnitConfig, setEditingUnitConfig] = useState(null);
  const [unitConfigFormData, setUnitConfigFormData] = useState({
    name: '',
    base_unit_name: 'units',
    enable_cases: true,
    case_unit_name: 'cases',
    units_per_case: 12,
    enable_pallets: true,
    pallet_unit_name: 'pallets',
    cases_per_pallet: 42
  });
  const [savingUnitConfig, setSavingUnitConfig] = useState(false);

  // Location details data
  const [locationCapacities, setLocationCapacities] = useState([]);

  // Accordion state management with memory persistence
  const [accordionStates, setAccordionStates] = useState(() => {
    const savedStates = localStorage.getItem(`productionLineAccordions_${lineProduction?.id || 'new'}`);
    return savedStates ? JSON.parse(savedStates) : {
      additionalIdentifiers: false,
      productTemplate: false,
      locationSchedule: false,
      productionDetails: false,
      productionNotes: false,
      timeline: false,
      templateBreakdown: false,
      componentAvailability: false
    };
  });

  // Pagination state for supplies
  const [showAllSupplies, setShowAllSupplies] = useState(false);

  // Update accordion state helper with memory persistence
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    
    // Save to localStorage
    localStorage.setItem(`productionLineAccordions_${lineProduction?.id || 'new'}`, JSON.stringify(newStates));
    
    // Reset supplies pagination when template breakdown accordion is collapsed
    if (accordionKey === 'templateBreakdown' && !expanded) {
      setShowAllSupplies(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openEditDialog: (isNew = false) => {
      handleEdit();
    }
  }));

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name');

      if (error) throw error;
      
      // Sort so main location is first, then alphabetically
      const sortedLocations = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setLocations(sortedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('production_line_templates')
        .select(`
          *,
          product:product_id(id, name, sku)
        `)
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchBatches = async () => {
    try {
      // First get all batches
      const { data: batchData, error: batchError } = await supabase
        .from('production_batches')
        .select(`
          *,
          product:product_id(id, name, sku),
          formulation:formulation_id(id, name, version)
        `)
        .eq('company_id', userCompanyId)
        .in('status', ['approved', 'scheduled', 'in_production', 'quality_testing', 'completed'])
        .order('created_at', { ascending: false });

      if (batchError) throw batchError;

      // Then get batch costs for each batch
      const batchesWithCosts = await Promise.all((batchData || []).map(async (batch) => {
        try {
          const { data: costsData } = await supabase
            .from('batch_costs')
            .select('*')
            .eq('batch_id', batch.id)
            .single();
          
          return {
            ...batch,
            batch_costs: costsData ? [costsData] : []
          };
        } catch (error) {
          return {
            ...batch,
            batch_costs: []
          };
        }
      }));

      setBatches(batchesWithCosts);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]);
    }
  };

  const fetchTemplateConsumption = async (templateId) => {
    if (!templateId) {
      setTemplateConsumption({
        caps: [],
        bottles: [],
        labels: [],
        supplies: []
      });
      return;
    }

    try {
      const [capsResult, bottlesResult, labelsResult, suppliesResult] = await Promise.all([
        supabase.from('template_caps_consumption').select('*, cap:cap_id(*)').eq('template_id', templateId),
        supabase.from('template_bottles_consumption').select('*, bottle:bottle_id(*)').eq('template_id', templateId),
        supabase.from('template_labels_consumption').select('*, label:label_id(*)').eq('template_id', templateId),
        supabase.from('template_supplies_consumption').select('*, supply:supply_id(*)').eq('template_id', templateId)
      ]);

      setTemplateConsumption({
        caps: capsResult.data?.map(item => ({ ...item.cap, quantity_per_unit: item.quantity_per_unit })) || [],
        bottles: bottlesResult.data?.map(item => ({ ...item.bottle, quantity_per_unit: item.quantity_per_unit })) || [],
        labels: labelsResult.data?.map(item => ({ ...item.label, quantity_per_unit: item.quantity_per_unit })) || [],
        supplies: suppliesResult.data?.map(item => ({ ...item.supply, quantity_per_unit: item.quantity_per_unit })) || []
      });
    } catch (error) {
      console.error('Error fetching template consumption:', error);
      setTemplateConsumption({
        caps: [],
        bottles: [],
        labels: [],
        supplies: []
      });
    }
  };

  const fetchInventoryData = async (locationId) => {
    if (!locationId) {
      setBottlesInventory([]);
      setCapsInventory([]);
      setLabelsInventory([]);
      setSuppliesInventory([]);
      setInventoryAnalysis(null);
      return;
    }

    try {
      // Fetch bottles inventory with correct table name
      const { data: bottles, error: bottlesError } = await supabase
        .from('bottle_inventory')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .gt('current_quantity', 0);

      if (bottlesError) throw bottlesError;
      setBottlesInventory(bottles || []);

      // Fetch caps inventory with correct table name
      const { data: caps, error: capsError } = await supabase
        .from('caps_inventory')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .gt('current_quantity', 0);

      if (capsError) throw capsError;
      setCapsInventory(caps || []);

      // Fetch labels inventory with correct table name
      const { data: labels, error: labelsError } = await supabase
        .from('label_inventory')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .gt('current_quantity', 0);

      if (labelsError) throw labelsError;
      setLabelsInventory(labels || []);

      // Fetch supplies inventory with correct table name
      const { data: supplies, error: suppliesError } = await supabase
        .from('supply_inventory')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('location_id', locationId)
        .gt('current_quantity', 0);

      if (suppliesError) throw suppliesError;
      setSuppliesInventory(supplies || []);

    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setBottlesInventory([]);
      setCapsInventory([]);
      setLabelsInventory([]);
      setSuppliesInventory([]);
    }
  };

  const fetchLocationDetails = async (locationId) => {
    if (!locationId) {
      setLocationCapacities([]);
      return;
    }

    try {
      // Load product capacities
      try {
        const { data: capacities, error: capacitiesError } = await supabase
          .from('location_product_capacities')
          .select(`
            *,
            product:product_id(
              name,
              sku
            )
          `)
          .eq('company_id', userCompanyId)
          .eq('location_id', locationId);

        if (capacitiesError && capacitiesError.code !== 'PGRST204') {
          console.error('Error loading capacities:', capacitiesError);
        } else {
          setLocationCapacities(capacities || []);
        }
      } catch (err) {
        console.error('Capacities table may not exist:', err);
      }

    } catch (err) {
      console.error('Error loading location details:', err);
    }
  };

  const calculateInventoryAnalysis = () => {
    if (!selectedUnitConfig || !editData.product_variant_id) {
      setInventoryAnalysis(null);
      return;
    }

    // Get the correct total from unit configuration inputs
    const totalUnits = calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]) || 0;
    
    const analysis = {
      totalUnitsNeeded: totalUnits,
      canMakeUnits: totalUnits,
      shortages: [],
      inventoryItems: []
    };

    // Calculate total quantity needed per unit from template consumption
    const bottlesPerUnit = templateConsumption.bottles.reduce((sum, b) => sum + (b.quantity_per_unit || 0), 0);
    const capsPerUnit = templateConsumption.caps.reduce((sum, c) => sum + (c.quantity_per_unit || 0), 0);
    const labelsPerUnit = templateConsumption.labels.reduce((sum, l) => sum + (l.quantity_per_unit || 0), 0);
    const suppliesPerUnit = templateConsumption.supplies.reduce((sum, s) => sum + (s.quantity_per_unit || 0), 0);

    // Analyze bottles (only if template has bottles configured)
    if (bottlesPerUnit > 0) {
      const bottlesNeeded = totalUnits * bottlesPerUnit;
      const bottlesAvailable = bottlesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      analysis.inventoryItems.push({
        type: 'Bottles',
        needed: bottlesNeeded,
        available: bottlesAvailable,
        canMake: bottlesAvailable >= bottlesNeeded,
        shortage: Math.max(0, bottlesNeeded - bottlesAvailable)
      });
    }

    // Analyze caps (only if template has caps configured)
    if (capsPerUnit > 0) {
      const capsNeeded = totalUnits * capsPerUnit;
      const capsAvailable = capsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      analysis.inventoryItems.push({
        type: 'Caps',
        needed: capsNeeded,
        available: capsAvailable,
        canMake: capsAvailable >= capsNeeded,
        shortage: Math.max(0, capsNeeded - capsAvailable)
      });
    }

    // Analyze labels (only if template has labels configured)
    if (labelsPerUnit > 0) {
      const labelsNeeded = totalUnits * labelsPerUnit;
      const labelsAvailable = labelsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      analysis.inventoryItems.push({
        type: 'Labels',
        needed: labelsNeeded,
        available: labelsAvailable,
        canMake: labelsAvailable >= labelsNeeded,
        shortage: Math.max(0, labelsNeeded - labelsAvailable)
      });
    }

    // Analyze supplies (only if template has supplies configured)
    if (suppliesPerUnit > 0) {
      const suppliesNeeded = totalUnits * suppliesPerUnit;
      const suppliesAvailable = suppliesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      analysis.inventoryItems.push({
        type: 'Supplies',
        needed: suppliesNeeded,
        available: suppliesAvailable,
        canMake: suppliesAvailable >= suppliesNeeded,
        shortage: Math.max(0, suppliesNeeded - suppliesAvailable)
      });
    }

    // Calculate max units we can actually make (only consider configured components)
    let maxUnits = totalUnits;
    if (bottlesPerUnit > 0) {
      const bottlesAvailable = bottlesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      maxUnits = Math.min(maxUnits, Math.floor(bottlesAvailable / bottlesPerUnit));
    }
    if (capsPerUnit > 0) {
      const capsAvailable = capsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      maxUnits = Math.min(maxUnits, Math.floor(capsAvailable / capsPerUnit));
    }
    if (labelsPerUnit > 0) {
      const labelsAvailable = labelsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      maxUnits = Math.min(maxUnits, Math.floor(labelsAvailable / labelsPerUnit));
    }
    if (suppliesPerUnit > 0) {
      const suppliesAvailable = suppliesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
      maxUnits = Math.min(maxUnits, Math.floor(suppliesAvailable / suppliesPerUnit));
    }
    analysis.canMakeUnits = maxUnits;

    // Check for shortages
    analysis.inventoryItems.forEach(item => {
      if (item.shortage > 0) {
        analysis.shortages.push(item);
      }
    });

    setInventoryAnalysis(analysis);
  };

  const fetchProductVariants = async (productId) => {
    try {
      // Fetch from product_variants table
      const { data: tableVariants, error: tableError } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true);

      if (tableError) throw tableError;

      // Also fetch the product to check for JSONB variants
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('variants, has_variants')
        .eq('id', productId)
        .single();

      if (productError) throw productError;

      let allVariants = [];

      // Add variants from product_variants table
      if (tableVariants && tableVariants.length > 0) {
        allVariants = tableVariants.map(variant => ({
          id: variant.id,
          name: variant.name,
          volume_weight: variant.volume_weight,
          volume_unit: variant.volume_unit || 'oz',
          price: variant.price,
          sku: variant.sku,
          source: 'table'
        }));
      }

      // Add variants from JSONB column if they exist
      if (productData?.variants && Array.isArray(productData.variants)) {
        const jsonbVariants = productData.variants.map(variant => ({
          id: variant.id || variant.name,
          name: variant.name,
          volume_weight: variant.volume_weight,
          volume_unit: variant.volume_unit || 'oz',
          price: variant.price,
          sku: variant.sku,
          source: 'jsonb'
        }));
        allVariants = [...allVariants, ...jsonbVariants];
      }

      // Sort variants by volume_weight descending (largest first: 16oz, 14oz, 12oz, etc.)
      const sortedVariants = allVariants.sort((a, b) => {
        const volumeA = parseFloat(a.volume_weight) || 0;
        const volumeB = parseFloat(b.volume_weight) || 0;
        return volumeB - volumeA; // Descending order (largest first)
      });

      setProductVariants(sortedVariants);
      return sortedVariants;
    } catch (error) {
      console.error('Error fetching product variants:', error);
      setProductVariants([]);
      return [];
    }
  };

  const fetchUnitConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      const sortedConfigs = (data || []).sort((a, b) => {
        const getTotalUnitsPerPallet = (config) => {
          if (config.enable_pallets && config.enable_cases) {
            return config.units_per_case * config.cases_per_pallet;
          } else if (config.enable_cases) {
            return config.units_per_case;
          } else {
            return 1;
          }
        };
        
        const aTotal = getTotalUnitsPerPallet(a);
        const bTotal = getTotalUnitsPerPallet(b);
        
        if (bTotal !== aTotal) {
          return bTotal - aTotal;
        }
        return a.name.localeCompare(b.name);
      });
      
      setUnitConfigurations(sortedConfigs);
    } catch (err) {
      console.error('Error fetching unit configurations:', err);
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchTemplates();
    fetchBatches();
    fetchUnitConfigurations();
    loadUserProfile();
  }, [userCompanyId]);
  
  const loadUserProfile = async () => {
    try {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('line_production_time_format_preference')
        .eq('id', user.id)
        .single();
        
      if (data && !error) {
        setUserProfile(data);
        setLineProductionTimeFormat(data.line_production_time_format_preference || '12hour');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setLineProductionTimeFormat('12hour');
    }
  };
  
  const updateLineProductionTimeFormat = async (newFormat) => {
    try {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('profiles')
        .update({ line_production_time_format_preference: newFormat })
        .eq('id', user.id);
        
      if (!error) {
        setLineProductionTimeFormat(newFormat);
        setUserProfile(prev => ({ ...prev, line_production_time_format_preference: newFormat }));
      } else {
        console.error('Error updating line production time format:', error);
      }
    } catch (error) {
      console.error('Error updating line production time format:', error);
    }
  };

  // Trigger inventory analysis when data changes
  useEffect(() => {
    if (selectedUnitConfig && editData.product_variant_id && editData.production_location) {
      calculateInventoryAnalysis();
    }
  }, [selectedUnitConfig, editData.product_variant_id, unitInputs, editData.production_location, bottlesInventory, capsInventory, labelsInventory, suppliesInventory, templateConsumption]);

  const handleProductChange = async (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    
    // Clear input values when product changes
    setVariantInputValues({});
    
    // Fetch variants for this product
    const variants = await fetchProductVariants(productId);
    
    setEditData({ 
      ...editData, 
      product_id: productId,
      units_to_make: 100,
      planned_quantity: 100,
      planned_unit_of_measure: 'units',
      variant_distribution: []
    });
  };

  const getVariantQuantity = (variantId) => {
    const distribution = editData.variant_distribution || [];
    const entry = distribution.find(d => d.variant_id === variantId);
    return entry ? entry.quantity : 0;
  };

  const getVariantInputValue = (variantId) => {
    // Return the local input value if it exists, otherwise return the actual quantity
    if (variantInputValues.hasOwnProperty(variantId)) {
      return variantInputValues[variantId];
    }
    return getVariantQuantity(variantId) || '';
  };

  // REMOVED OLD VARIANT INPUT CHANGE - ONLY UNIT CONFIG INPUTS MATTER


  const calculateTotalUnitsFromInputs = (variantId, inputsOverride = null) => {
    if (!selectedUnitConfig || !variantId) return 0;
    
    const variantInputs = inputsOverride || unitInputs[variantId] || { pallets: 0, cases: 0, units: 0 };
    let total = 0;
    
    // Add units from pallets (only if both pallets and cases are enabled)
    if (selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases) {
      total += (variantInputs.pallets || 0) * selectedUnitConfig.cases_per_pallet * selectedUnitConfig.units_per_case;
    }
    
    // Add units from cases (independent cases, not from pallets)
    if (selectedUnitConfig.enable_cases) {
      total += (variantInputs.cases || 0) * selectedUnitConfig.units_per_case;
    }
    
    // Add individual units
    total += (variantInputs.units || 0);
    
    return total;
  };

  const updateVariantFromUnitInputs = (variantId, newInputs) => {
    if (!selectedUnitConfig) return;
    
    const totalUnits = calculateTotalUnitsFromInputs(variantId, newInputs);
    
    const currentDistribution = editData.variant_distribution || [];
    let newDistribution = [...currentDistribution];
    
    const existingIndex = newDistribution.findIndex(d => d.variant_id === variantId);
    
    if (totalUnits > 0) {
      if (existingIndex >= 0) {
        newDistribution[existingIndex] = { variant_id: variantId, quantity: totalUnits };
      } else {
        newDistribution.push({ variant_id: variantId, quantity: totalUnits });
      }
    } else {
      if (existingIndex >= 0) {
        newDistribution.splice(existingIndex, 1);
      }
    }
    
    // Calculate total from ALL variants in distribution
    const totalFromAllVariants = newDistribution.reduce((sum, variant) => sum + variant.quantity, 0);
    
    setEditData({
      ...editData,
      variant_distribution: newDistribution,
      planned_quantity: totalFromAllVariants,
      planned_unit_of_measure: 'units',
      units_to_make: totalFromAllVariants
    });
  };

  const handleUnitConfigSubmit = async () => {
    setSavingUnitConfig(true);
    
    try {
      if (!unitConfigFormData.name) {
        throw new Error('Please enter a configuration name');
      }

      if (unitConfigFormData.enable_cases && unitConfigFormData.units_per_case < 1) {
        throw new Error('Units per case must be at least 1');
      }

      if (unitConfigFormData.enable_pallets && unitConfigFormData.cases_per_pallet < 1) {
        throw new Error('Cases per pallet must be at least 1');
      }

      const configData = {
        company_id: userCompanyId,
        name: unitConfigFormData.name,
        base_unit_name: unitConfigFormData.base_unit_name,
        enable_cases: unitConfigFormData.enable_cases,
        case_unit_name: unitConfigFormData.enable_cases ? unitConfigFormData.case_unit_name : null,
        units_per_case: unitConfigFormData.enable_cases ? parseInt(unitConfigFormData.units_per_case) : 1,
        enable_pallets: unitConfigFormData.enable_pallets,
        pallet_unit_name: unitConfigFormData.enable_pallets ? unitConfigFormData.pallet_unit_name : null,
        cases_per_pallet: unitConfigFormData.enable_pallets ? parseInt(unitConfigFormData.cases_per_pallet) : 1
      };

      if (editingUnitConfig) {
        const { error } = await supabase
          .from('unit_configurations')
          .update(configData)
          .eq('id', editingUnitConfig.id);

        if (error) throw error;
        showSnackbar('Unit configuration updated successfully', 'success');
      } else {
        const { error } = await supabase
          .from('unit_configurations')
          .insert([configData]);

        if (error) throw error;
        showSnackbar('Unit configuration created successfully', 'success');
      }

      handleCloseUnitConfigDialog();
      await fetchUnitConfigurations();
    } catch (err) {
      console.error('Error saving unit configuration:', err);
      showSnackbar(err.message || 'Failed to save unit configuration', 'error');
    } finally {
      setSavingUnitConfig(false);
    }
  };

  const handleCloseUnitConfigDialog = () => {
    setUnitConfigDialogOpen(false);
    setEditingUnitConfig(null);
    setUnitConfigFormData({
      name: '',
      base_unit_name: 'units',
      enable_cases: true,
      case_unit_name: 'cases',
      units_per_case: 12,
      enable_pallets: true,
      pallet_unit_name: 'pallets',
      cases_per_pallet: 42
    });
  };

  // REMOVED OLD DISTRIBUTION CALCULATION - ONLY UNIT CONFIG MATTERS NOW

  // Product dialog handlers
  const handleOpenProductSelection = () => {
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
    setProductSelectionDialogOpen(true);
  };

  const handleCloseProductSelection = () => {
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
  };

  const handleSelectProduct = async (product) => {
    setDialogSelectedProduct(product);
    await fetchProductVariants(product.id);
    // Auto-select product in editData for immediate feedback
    setEditData(prev => ({
      ...prev,
      product_id: product.id,
      product_variant_id: null,
      product_variant: ''
    }));
  };

  const handleConfirmProductSelection = async () => {
    if (dialogSelectedProduct) {
      // Only call handleProductChange if we haven't already set the product
      if (editData.product_id !== dialogSelectedProduct.id) {
        await handleProductChange(dialogSelectedProduct.id);
      }
      handleCloseProductSelection();
    }
  };

  const handleVariantChange = (variantId) => {
    const variant = productVariants.find(v => v.id === variantId);
    if (!variant) return;
    
    setEditData({
      ...editData,
      product_variant_id: variantId,
      product_variant: variant.name || 'Variant'
    });
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;
    
    const searchTerm = productSearchTerm.toLowerCase();
    return products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm) ||
      product.description?.toLowerCase().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm)
    );
  };

  // Location dialog handlers
  const handleOpenLocationSelection = () => {
    setLocationSearchTerm('');
    setLocationSelectionDialogOpen(true);
  };

  const handleCloseLocationSelection = () => {
    setLocationSelectionDialogOpen(false);
    setLocationSearchTerm('');
  };

  const handleSelectLocation = async (location) => {
    setEditData({ ...editData, production_location: location.id });
    await fetchInventoryData(location.id);
    await fetchLocationDetails(location.id);
    handleCloseLocationSelection();
  };

  const getFilteredLocations = () => {
    const filteredLocations = !locationSearchTerm.trim() 
      ? locations 
      : (() => {
          const searchTerm = locationSearchTerm.toLowerCase();
          return locations.filter(location => 
            location.name?.toLowerCase().includes(searchTerm) ||
            location.facility_type?.toLowerCase().includes(searchTerm) ||
            location.city?.toLowerCase().includes(searchTerm) ||
            location.state?.toLowerCase().includes(searchTerm)
          );
        })();
    
    // Ensure main location is always first, then alphabetically
    return filteredLocations.sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Unit Configuration Selection Dialog handlers
  const handleOpenUnitConfigSelection = () => {
    setUnitConfigSearchTerm('');
    setUnitConfigSelectionDialogOpen(true);
  };

  const handleCloseUnitConfigSelection = () => {
    setUnitConfigSelectionDialogOpen(false);
    setUnitConfigSearchTerm('');
  };

  const handleSelectUnitConfig = (config) => {
    setSelectedUnitConfig(config);
    setUnitInputs({});
    handleCloseUnitConfigSelection();
  };

  const getFilteredUnitConfigurations = () => {
    if (!unitConfigSearchTerm.trim()) return unitConfigurations;
    
    const searchTerm = unitConfigSearchTerm.toLowerCase();
    return unitConfigurations.filter(config => 
      config.name?.toLowerCase().includes(searchTerm) ||
      config.base_unit_name?.toLowerCase().includes(searchTerm) ||
      config.case_unit_name?.toLowerCase().includes(searchTerm) ||
      config.pallet_unit_name?.toLowerCase().includes(searchTerm)
    );
  };

  // Template selection handlers
  const handleOpenTemplateSelection = () => {
    setTemplateSelectionDialogOpen(true);
    setTemplateSearchTerm('');
  };

  const handleCloseTemplateSelection = () => {
    setTemplateSelectionDialogOpen(false);
    setTemplateSearchTerm('');
  };

  const handleTemplateSelect = async (template) => {
    setEditData({ ...editData, template_id: template.id });
    await fetchTemplateConsumption(template.id);
    setShowAllSupplies(false); // Reset supplies pagination when template changes
    handleCloseTemplateSelection();
  };

  const getFilteredTemplates = () => {
    if (!templateSearchTerm.trim()) return templates;
    
    const searchTerm = templateSearchTerm.toLowerCase();
    return templates.filter(template => 
      template.template_name?.toLowerCase().includes(searchTerm) ||
      template.description?.toLowerCase().includes(searchTerm) ||
      template.product?.name?.toLowerCase().includes(searchTerm)
    );
  };

  // Batch selection handlers
  const handleOpenBatchSelection = () => {
    setBatchSelectionDialogOpen(true);
    setBatchSearchTerm('');
  };

  const handleCloseBatchSelection = () => {
    setBatchSelectionDialogOpen(false);
    setBatchSearchTerm('');
    setSelectedBatch(null);
  };

  const handleBatchSelect = async (batch) => {
    setSelectedBatch(batch);
    
    try {
      // Fetch comprehensive cost data from batch_costs table
      const { data: batchCostsData, error } = await supabase
        .from('batch_costs')
        .select('*')
        .eq('batch_id', batch.id)
        .single();
      
      let totalCost = 0;
      let costPerUnit = 0;
      
      if (!error && batchCostsData) {
        // Use comprehensive cost data from batch_costs table
        totalCost = batchCostsData.total_production_batch_cost || batchCostsData.total_cost || 0;
        // Always recalculate cost per unit using units_to_make
        const totalUnits = batch.units_to_make || 0;
        costPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
      } else {
        // Fallback to production_batches cost data
                        const totalUnits = batch.units_to_make || 0; // Number of product units, not oz
                        totalCost = batch.actual_material_cost || batch.planned_material_cost || 0;
                        costPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
      }
      
      // Calculate expiration date from batch production date + shelf life
      let calculatedExpirationDate = editData.expiration_date;
      if (batch.expiration_date) {
        calculatedExpirationDate = batch.expiration_date;
      } else if (batch.production_date && batch.shelf_life_days) {
        const productionDate = new Date(batch.production_date);
        const expirationDate = new Date(productionDate);
        expirationDate.setDate(productionDate.getDate() + batch.shelf_life_days);
        calculatedExpirationDate = expirationDate.toISOString().split('T')[0];
      }

      // Auto-populate product and variants if available
      let productVariantsData = [];
      if (batch.product_id) {
        productVariantsData = await fetchProductVariants(batch.product_id);
        setSelectedProduct(batch.product);
      }

      // Load unit configuration if batch has one
      if (batch.unit_configuration_id) {
        const unitConfig = unitConfigurations.find(config => config.id === batch.unit_configuration_id);
        if (unitConfig) {
          setSelectedUnitConfig(unitConfig);
          
          // Auto-populate unit inputs from batch data
          if (batch.product_variant_id) {
            const unitInputsForVariant = {};
            
            // Set unit inputs based on batch quantities
            if (batch.pallet_quantity && batch.pallet_quantity > 0) {
              unitInputsForVariant.pallets = Math.floor(batch.pallet_quantity);
            }
            if (batch.case_quantity && batch.case_quantity > 0) {
              unitInputsForVariant.cases = batch.case_quantity;
            }
            if (batch.unit_quantity && batch.unit_quantity > 0) {
              unitInputsForVariant.units = batch.unit_quantity;
            }
            
            setUnitInputs({ [batch.product_variant_id]: unitInputsForVariant });
          }
        }
      }

      // Set up variant distribution from batch data
      let variantDistribution = [];
      if (batch.product_variant_id && batch.planned_quantity) {
        variantDistribution = [{
          variant_id: batch.product_variant_id,
          quantity: batch.planned_quantity
        }];
      } else if (batch.variant_distribution && Array.isArray(batch.variant_distribution)) {
        variantDistribution = batch.variant_distribution;
      }
      
      setEditData({ 
        ...editData, 
        batch_id: batch.id,
        batch_total_material_cost: totalCost,
        batch_cost_per_unit: costPerUnit,
        expiration_date: calculatedExpirationDate,
        // Auto-populate from batch
        batch_number: batch.batch_number || editData.batch_number,
        lot_number: batch.lot_number || editData.lot_number,
        internal_code: batch.internal_code || editData.internal_code,
        product_id: batch.product_id || editData.product_id,
        product_variant_id: batch.product_variant_id || editData.product_variant_id,
        product_variant: batch.product_variant || editData.product_variant,
        variant_distribution: variantDistribution,
        planned_quantity: batch.planned_quantity || editData.planned_quantity,
        planned_unit_of_measure: batch.planned_unit_of_measure || editData.planned_unit_of_measure,
        units_to_make: batch.planned_quantity || editData.units_to_make,
        unit_configuration_id: batch.unit_configuration_id || editData.unit_configuration_id
      });
      
    } catch (error) {
      console.error('Error fetching batch costs:', error);
      // Fallback to basic cost data
      const totalQuantity = batch.planned_quantity || 0;
      const totalCost = batch.actual_material_cost || batch.planned_material_cost || 0;
      const costPerUnit = totalQuantity > 0 ? totalCost / totalQuantity : 0;
      
      // Calculate expiration date from batch production date + shelf life
      let calculatedExpirationDate = editData.expiration_date;
      if (batch.expiration_date) {
        calculatedExpirationDate = batch.expiration_date;
      } else if (batch.production_date && batch.shelf_life_days) {
        const productionDate = new Date(batch.production_date);
        const expirationDate = new Date(productionDate);
        expirationDate.setDate(productionDate.getDate() + batch.shelf_life_days);
        calculatedExpirationDate = expirationDate.toISOString().split('T')[0];
      }

      // Auto-populate product and variants if available
      let productVariantsData = [];
      if (batch.product_id) {
        try {
          productVariantsData = await fetchProductVariants(batch.product_id);
          setSelectedProduct(batch.product);
        } catch (err) {
          console.error('Error fetching product variants:', err);
        }
      }

      // Load unit configuration if batch has one
      if (batch.unit_configuration_id) {
        const unitConfig = unitConfigurations.find(config => config.id === batch.unit_configuration_id);
        if (unitConfig) {
          setSelectedUnitConfig(unitConfig);
          
          // Auto-populate unit inputs from batch data
          if (batch.product_variant_id) {
            const unitInputsForVariant = {};
            
            // Set unit inputs based on batch quantities
            if (batch.pallet_quantity && batch.pallet_quantity > 0) {
              unitInputsForVariant.pallets = Math.floor(batch.pallet_quantity);
            }
            if (batch.case_quantity && batch.case_quantity > 0) {
              unitInputsForVariant.cases = batch.case_quantity;
            }
            if (batch.unit_quantity && batch.unit_quantity > 0) {
              unitInputsForVariant.units = batch.unit_quantity;
            }
            
            setUnitInputs({ [batch.product_variant_id]: unitInputsForVariant });
          }
        }
      }

      // Set up variant distribution from batch data
      let variantDistribution = [];
      if (batch.product_variant_id && batch.planned_quantity) {
        variantDistribution = [{
          variant_id: batch.product_variant_id,
          quantity: batch.planned_quantity
        }];
      } else if (batch.variant_distribution && Array.isArray(batch.variant_distribution)) {
        variantDistribution = batch.variant_distribution;
      }
      
      setEditData({ 
        ...editData, 
        batch_id: batch.id,
        batch_total_material_cost: totalCost,
        batch_cost_per_unit: costPerUnit,
        expiration_date: calculatedExpirationDate,
        // Auto-populate from batch
        batch_number: batch.batch_number || editData.batch_number,
        lot_number: batch.lot_number || editData.lot_number,
        internal_code: batch.internal_code || editData.internal_code,
        product_id: batch.product_id || editData.product_id,
        product_variant_id: batch.product_variant_id || editData.product_variant_id,
        product_variant: batch.product_variant || editData.product_variant,
        variant_distribution: variantDistribution,
        planned_quantity: batch.planned_quantity || editData.planned_quantity,
        planned_unit_of_measure: batch.planned_unit_of_measure || editData.planned_unit_of_measure,
        units_to_make: batch.planned_quantity || editData.units_to_make,
        unit_configuration_id: batch.unit_configuration_id || editData.unit_configuration_id
      });
    }
    
    handleCloseBatchSelection();
  };

  const getFilteredBatches = () => {
    let filtered = batches;
    
    if (batchSearchTerm.trim()) {
      const searchTerm = batchSearchTerm.toLowerCase();
      filtered = batches.filter(batch => 
        batch.batch_number?.toLowerCase().includes(searchTerm) ||
        batch.lot_number?.toLowerCase().includes(searchTerm) ||
        batch.product?.name?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Sort by most recent (created_at descending)
    return [...filtered].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Descending order (most recent first)
    });
  };

  const getMatchingBatches = () => {
    if (!editData.batch_number && !editData.lot_number) return [];
    
    return batches.filter(batch => 
      (editData.batch_number && batch.batch_number === editData.batch_number) ||
      (editData.lot_number && batch.lot_number === editData.lot_number)
    );
  };

  const handleEdit = async () => {
    // Default to main location if creating new and no location set
    let defaultLocation = lineProduction?.production_location || '';
    if (!defaultLocation && !lineProduction) {
      const mainLocation = locations.find(loc => loc.is_main);
      if (mainLocation) {
        defaultLocation = mainLocation.id;
      }
    }
    
    setEditData({
      batch_number: lineProduction?.batch_number || '',
      lot_number: lineProduction?.lot_number || '',
      internal_code: lineProduction?.internal_code || '',
      product_id: lineProduction?.product_id || '',
      planned_quantity: lineProduction?.planned_quantity || 0,
      planned_unit_of_measure: lineProduction?.planned_unit_of_measure || 'units',
      product_variant: lineProduction?.product_variant || 'Variant',
      product_variant_id: lineProduction?.product_variant_id || null,
      units_to_make: lineProduction?.units_to_make || 100,
      variant_distribution: lineProduction?.variant_distribution || [],
      production_location: defaultLocation,
      template_id: lineProduction?.template_id || '',
      manufacture_date: formatDateForInput(lineProduction?.manufacture_date) || new Date().toISOString().split('T')[0],
      expiration_date: formatDateForInput(lineProduction?.expiration_date) || '',
      status: lineProduction?.status || 'draft',
      priority: lineProduction?.priority || 'normal',
      production_line: lineProduction?.production_line || '',
      shift_supervisor: lineProduction?.shift_supervisor || '',
      production_notes: lineProduction?.production_notes || '',
      planned_start_datetime: lineProduction?.planned_start_date && lineProduction?.planned_start_time 
        ? dayjs(`${lineProduction.planned_start_date} ${lineProduction.planned_start_time}`, 'YYYY-MM-DD HH:mm') 
        : null,
      planned_completion_datetime: lineProduction?.planned_completion_date && lineProduction?.planned_completion_time 
        ? dayjs(`${lineProduction.planned_completion_date} ${lineProduction.planned_completion_time}`, 'YYYY-MM-DD HH:mm') 
        : null,
      batch_id: lineProduction?.batch_id || '',
      batch_total_material_cost: lineProduction?.batch_total_material_cost || 0,
      batch_cost_per_unit: lineProduction?.batch_cost_per_unit || 0,
      unit_configuration_id: lineProduction?.unit_configuration_id || null
    });

    if (lineProduction?.unit_configuration_id) {
      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
      setSelectedUnitConfig(unitConfig || null);
      
      // Auto-populate unit inputs from STORED VALUES
      if (lineProduction.product_variant_id) {
        const unitInputsForVariant = {
          pallets: lineProduction.pallet_quantity || 0,
          cases: lineProduction.case_quantity || 0,
          units: lineProduction.unit_quantity || 0
        };
        
        setUnitInputs({ [lineProduction.product_variant_id]: unitInputsForVariant });
      }
    } else {
      setSelectedUnitConfig(null);
    }
    
    setValidationError('');
    setActiveStep(0); // Reset to first step
    setEditDialogOpen(true);
  };

  useEffect(() => {
    if (isCreatingNew) {
      handleEdit();
    }
  }, [isCreatingNew]);

  // Load data for existing line production analysis
  useEffect(() => {
    if (lineProduction && !isCreatingNew) {
      // Load product data for analysis
      if (lineProduction.product_id) {
        const product = products.find(p => p.id === lineProduction.product_id);
        setSelectedProduct(product);
        fetchProductVariants(lineProduction.product_id);
      }
      
      // Load inventory data for analysis
      if (lineProduction.production_location) {
        fetchInventoryData(lineProduction.production_location);
        fetchLocationDetails(lineProduction.production_location);
      }

      // Load template consumption data
      if (lineProduction.template_id) {
        fetchTemplateConsumption(lineProduction.template_id);
      }
    }
  }, [lineProduction, products, isCreatingNew]);

  // Calculate inventory analysis for existing line production
  useEffect(() => {
    if (lineProduction && lineProduction.unit_configuration_id) {
      // Calculate from stored unit configuration values
      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
      let totalUnits = 0;
      
      if (unitConfig) {
        const pallets = lineProduction.pallet_quantity || 0;
        const cases = lineProduction.case_quantity || 0;
        const units = lineProduction.unit_quantity || 0;
        
        if (unitConfig.enable_pallets && unitConfig.enable_cases) {
          totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
        }
        if (unitConfig.enable_cases) {
          totalUnits += cases * unitConfig.units_per_case;
        }
        totalUnits += units;
      } else {
        // Fallback to variant distribution
        totalUnits = lineProduction.variant_distribution ? 
          lineProduction.variant_distribution.reduce((sum, variant) => sum + variant.quantity, 0) : 
          lineProduction.planned_quantity || 0;
      }
      
      const analysis = {
        totalUnitsNeeded: totalUnits,
        canMakeUnits: totalUnits,
        shortages: [],
        inventoryItems: []
      };

      // Calculate total quantity needed per unit from template consumption
      const bottlesPerUnit = templateConsumption.bottles.reduce((sum, b) => sum + (b.quantity_per_unit || 0), 0);
      const capsPerUnit = templateConsumption.caps.reduce((sum, c) => sum + (c.quantity_per_unit || 0), 0);
      const labelsPerUnit = templateConsumption.labels.reduce((sum, l) => sum + (l.quantity_per_unit || 0), 0);
      const suppliesPerUnit = templateConsumption.supplies.reduce((sum, s) => sum + (s.quantity_per_unit || 0), 0);

      // Analyze bottles (only if template has bottles configured)
      if (bottlesPerUnit > 0) {
        const bottlesNeeded = totalUnits * bottlesPerUnit;
        const bottlesAvailable = bottlesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        analysis.inventoryItems.push({
          type: 'Bottles',
          needed: bottlesNeeded,
          available: bottlesAvailable,
          canMake: bottlesAvailable >= bottlesNeeded,
          shortage: Math.max(0, bottlesNeeded - bottlesAvailable)
        });
      }

      // Analyze caps (only if template has caps configured)
      if (capsPerUnit > 0) {
        const capsNeeded = totalUnits * capsPerUnit;
        const capsAvailable = capsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        analysis.inventoryItems.push({
          type: 'Caps',
          needed: capsNeeded,
          available: capsAvailable,
          canMake: capsAvailable >= capsNeeded,
          shortage: Math.max(0, capsNeeded - capsAvailable)
        });
      }

      // Analyze labels (only if template has labels configured)
      if (labelsPerUnit > 0) {
        const labelsNeeded = totalUnits * labelsPerUnit;
        const labelsAvailable = labelsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        analysis.inventoryItems.push({
          type: 'Labels',
          needed: labelsNeeded,
          available: labelsAvailable,
          canMake: labelsAvailable >= labelsNeeded,
          shortage: Math.max(0, labelsNeeded - labelsAvailable)
        });
      }

      // Analyze supplies (only if template has supplies configured)
      if (suppliesPerUnit > 0) {
        const suppliesNeeded = totalUnits * suppliesPerUnit;
        const suppliesAvailable = suppliesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        analysis.inventoryItems.push({
          type: 'Supplies',
          needed: suppliesNeeded,
          available: suppliesAvailable,
          canMake: suppliesAvailable >= suppliesNeeded,
          shortage: Math.max(0, suppliesNeeded - suppliesAvailable)
        });
      }

      // Calculate max units we can actually make (only consider configured components)
      let maxUnits = totalUnits;
      if (bottlesPerUnit > 0) {
        const bottlesAvailable = bottlesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        maxUnits = Math.min(maxUnits, Math.floor(bottlesAvailable / bottlesPerUnit));
      }
      if (capsPerUnit > 0) {
        const capsAvailable = capsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        maxUnits = Math.min(maxUnits, Math.floor(capsAvailable / capsPerUnit));
      }
      if (labelsPerUnit > 0) {
        const labelsAvailable = labelsInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        maxUnits = Math.min(maxUnits, Math.floor(labelsAvailable / labelsPerUnit));
      }
      if (suppliesPerUnit > 0) {
        const suppliesAvailable = suppliesInventory.reduce((sum, item) => sum + (item.current_quantity || 0), 0);
        maxUnits = Math.min(maxUnits, Math.floor(suppliesAvailable / suppliesPerUnit));
      }
      analysis.canMakeUnits = maxUnits;

      // Check for shortages
      analysis.inventoryItems.forEach(item => {
        if (item.shortage > 0) {
          analysis.shortages.push(item);
        }
      });

      setInventoryAnalysis(analysis);
    }
  }, [lineProduction?.unit_configuration_id, lineProduction?.pallet_quantity, lineProduction?.case_quantity, lineProduction?.unit_quantity, unitConfigurations, bottlesInventory, capsInventory, labelsInventory, suppliesInventory, templateConsumption]);

  const isFormValid = () => {
    return (
      editData.batch_number?.trim() &&
      editData.lot_number?.trim() &&
      editData.status &&
      editData.product_id &&
      editData.production_location &&
      editData.template_id &&
      editData.manufacture_date &&
      editData.expiration_date &&
      editData.planned_start_datetime &&
      editData.planned_completion_datetime &&
      editData.priority &&
      editData.production_line?.trim() &&
      editData.shift_supervisor?.trim() &&
      selectedUnitConfig &&
      editData.product_variant_id &&
      calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]) > 0
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setValidationError('');
    
    try {
      // Validate required fields
      const requiredFields = [];
      
      if (!editData.batch_number?.trim()) requiredFields.push('Batch Number');
      if (!editData.lot_number?.trim()) requiredFields.push('Lot Number');
      if (!editData.status) requiredFields.push('Status');
      if (!editData.product_id) requiredFields.push('Product');
      if (!editData.production_location) requiredFields.push('Production Location');
      if (!editData.template_id) requiredFields.push('Production Template');
      if (!editData.manufacture_date) requiredFields.push('Manufacture Date');
      if (!editData.expiration_date) requiredFields.push('Expiration Date');
      if (!editData.planned_start_datetime) requiredFields.push('Planned Start Date & Time');
      if (!editData.planned_completion_datetime) requiredFields.push('Planned Completion Date & Time');
      if (!editData.priority) requiredFields.push('Priority');
      if (!editData.production_line?.trim()) requiredFields.push('Production Line');
      if (!editData.shift_supervisor?.trim()) requiredFields.push('Shift Supervisor');
      if (!selectedUnitConfig || !editData.product_variant_id) {
        requiredFields.push('Unit Configuration and Product Variant must be selected');
      } else if (calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]) === 0) {
        requiredFields.push('Unit Configuration Quantities (must configure at least some pallets, cases, or units)');
      }

      if (requiredFields.length > 0) {
        const errorMessage = `Please fill in the following required fields:\n• ${requiredFields.join('\n• ')}`;
        setValidationError(errorMessage);
        showSnackbar(errorMessage, 'error');
        setSaving(false);
        return;
      }

      const cleanedData = { ...editData };
      if (cleanedData.expiration_date === '') cleanedData.expiration_date = null;
      
      // Get the actual unit inputs that the user entered
      let palletQuantity = 0;
      let caseQuantity = 0; 
      let unitQuantity = 0;
      let totalUnitsFromInputs = 0;
      
      if (selectedUnitConfig && editData.product_variant_id) {
        const inputs = unitInputs[editData.product_variant_id] || {};
        
        // Store the EXACT values the user entered
        palletQuantity = inputs.pallets || 0;
        caseQuantity = inputs.cases || 0;
        unitQuantity = inputs.units || 0;
        
        // Calculate total for verification
        totalUnitsFromInputs = calculateTotalUnitsFromInputs(editData.product_variant_id, inputs);
      }
      
      const lineProductionData = {
        ...cleanedData,
        planned_start_date: cleanedData.planned_start_datetime ? cleanedData.planned_start_datetime.format('YYYY-MM-DD') : null,
        planned_start_time: cleanedData.planned_start_datetime ? cleanedData.planned_start_datetime.format('HH:mm') : null,
        planned_completion_date: cleanedData.planned_completion_datetime ? cleanedData.planned_completion_datetime.format('YYYY-MM-DD') : null,
        planned_completion_time: cleanedData.planned_completion_datetime ? cleanedData.planned_completion_datetime.format('HH:mm') : null,
        unit_configuration_id: selectedUnitConfig?.id || null,
        pallet_quantity: palletQuantity,
        case_quantity: caseQuantity,
        unit_quantity: unitQuantity,
        planned_quantity: totalUnitsFromInputs,
        units_to_make: totalUnitsFromInputs,
        company_id: userCompanyId,
        updated_by: user?.id,
        updated_at: new Date().toISOString(),
        batch_id: cleanedData.batch_id || null,
        batch_total_material_cost: cleanedData.batch_total_material_cost || null,
        batch_cost_per_unit: cleanedData.batch_cost_per_unit || null
      };
      
      delete lineProductionData.planned_start_datetime;
      delete lineProductionData.planned_completion_datetime;

      if (isCreatingNew) {
        lineProductionData.created_by = user?.id;
        lineProductionData.created_at = new Date().toISOString();
      }

      let result;
      if (lineProduction?.id) {
        const { data, error } = await supabase
          .from('line_productions')
          .update(lineProductionData)
          .eq('id', lineProduction.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('line_productions')
          .insert([lineProductionData])
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      setEditDialogOpen(false);
      setVariantInputValues({}); // Clear input values after successful save
      onUpdate(result.id);
      showSnackbar(`Line production ${isCreatingNew ? 'created' : 'updated'} successfully!`, 'success');
    } catch (error) {
      console.error('Error saving line production:', error);
      
      let errorMessage = `Error ${isCreatingNew ? 'creating' : 'updating'} line production: `;
      
      errorMessage += 'An unexpected error occurred. Please try again or contact support if the problem persists.';
      
      setValidationError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setActiveStep(0);
    setVariantInputValues({}); // Clear input values
    if (isCreatingNew && onBackToList) {
      onBackToList();
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => {
    setActiveStep(step);
  };

  const steps = [
    'Basic Information',
    'Product Selection',
    'Unit Configuration',
    'Location & Template',
    'Dates & Schedule',
    'Production Details'
  ];

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  const getLocationLabel = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      return location.name;
    }
    return locationId || 'Not set';
  };

  // Permission check - for creating new: need both view AND edit, for existing: only need view
  if (!canViewLineOverview() || (isCreatingNew && !canEditLineOverview())) {
    return (
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100vw',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#f5f5f5'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          width: '100%',
          maxWidth: 600,
          px: 3
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Line Assembly Overview Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Line Assembly Overview. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }
  
  // Check edit permissions  
  const canEdit = canEditLineOverview();

  if (!lineProduction && !isCreatingNew) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No line production data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Enhanced Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <FactoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    {lineProduction?.batch_number || 'New Line Production'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {lineProduction?.product?.name || 'Product not selected'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Time</InputLabel>
                <Select
                  value={lineProductionTimeFormat}
                  onChange={(e) => updateLineProductionTimeFormat(e.target.value)}
                  label="Time"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <MenuItem value="12hour">12 Hour</MenuItem>
                  <MenuItem value="24hour">24 Hour</MenuItem>
                </Select>
              </FormControl>
              {canEdit && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  size="large"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Edit Line Production
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 3-Panel Layout */}
      <Grid container spacing={5}>
        {/* PANEL A: Line Production Info Card */}
        <Grid item xs={12} lg={4}>
          <Card 
            sx={{ 
              height: 'fit-content',
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Line Production Information
                </Typography>
              </Box>
              
              <Stack spacing={3}>
                {/* Core Information - Always Visible */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 2, display: 'block' }}>
                    CORE INFORMATION
                  </Typography>
                  <Stack spacing={2}>
                    <Chip 
                      icon={<FactoryIcon />}
                      label={`Batch: ${lineProduction?.batch_number || 'Not set'}`}
                      variant="outlined"
                      sx={{ 
                        justifyContent: 'flex-start', 
                        fontSize: '0.875rem', 
                        py: 1,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        '& .MuiChip-icon': { color: theme.palette.primary.main },
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Chip 
                        label={`Status: ${formatStatus(lineProduction?.status)}`}
                        sx={{ 
                          fontWeight: 500,
                          borderRadius: 2,
                          background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          color: 'white',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            background: `linear-gradient(${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                          },
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                      <Chip 
                        label={`Priority: ${lineProduction?.priority?.toUpperCase() || 'NORMAL'}`}
                        variant="outlined"
                        sx={{ 
                          fontWeight: 500,
                          borderRadius: 2,
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: theme.palette.primary.main,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          },
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>

                {/* Collapsible Identification Details */}
                {lineProduction?.lot_number && (
                  <Accordion 
                    expanded={accordionStates.additionalIdentifiers}
                    onChange={(event, expanded) => updateAccordionState('additionalIdentifiers', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        borderRadius: '12px 12px 0 0',
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        minHeight: 72,
                        '&.Mui-expanded': {
                          minHeight: 72,
                          borderRadius: '12px 12px 0 0'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '16px 0',
                          alignItems: 'center',
                          '&.Mui-expanded': {
                            margin: '16px 0'
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Additional Identifiers
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 4 }}>
                      <Stack spacing={1.5}>
                        <Chip 
                          icon={<InventoryIcon />}
                          label={`Lot: ${lineProduction.lot_number}`}
                          variant="outlined"
                          sx={{ 
                            justifyContent: 'flex-start', 
                            fontSize: '0.875rem', 
                            py: 1,
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.main,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiChip-icon': { color: theme.palette.primary.main },
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />
                        {lineProduction?.internal_code && (
                          <Chip 
                            icon={<AssignmentIcon />}
                            label={`Code: ${lineProduction.internal_code}`}
                            variant="outlined"
                            sx={{ 
                              justifyContent: 'flex-start', 
                              fontSize: '0.875rem', 
                              py: 1,
                              borderRadius: 2,
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                              color: theme.palette.primary.main,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                              '& .MuiChip-icon': { color: theme.palette.primary.main },
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              },
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                          />
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Product & Template */}
                <Accordion 
                  expanded={accordionStates.productTemplate}
                  onChange={(event, expanded) => updateAccordionState('productTemplate', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      borderRadius: '8px 8px 0 0',
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56,
                        borderRadius: '8px 8px 0 0'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Product & Template
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                          PRODUCT
                        </Typography>
                        <Paper sx={{ 
                          p: 2, 
                          background: `white`,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                            {lineProduction?.product?.name || 'Not selected'}
                          </Typography>
                          {lineProduction?.variant_distribution && lineProduction.variant_distribution.length > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {lineProduction.variant_distribution.map(d => {
                                const variant = productVariants.find(v => v.id === d.variant_id);
                                return variant ? `${d.quantity} × ${variant.name || 'Variant'}` : '';
                              }).filter(Boolean).join(' + ')}
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                          TEMPLATE
                        </Typography>
                        <Paper sx={{ 
                          p: 2, 
                          background: `white`,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5, color: theme.palette.text.primary }}>
                            {templates.find(t => t.id === lineProduction?.template_id)?.template_name || 'Not selected'}
                          </Typography>
                          {lineProduction?.template_id && templates.find(t => t.id === lineProduction.template_id)?.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {templates.find(t => t.id === lineProduction.template_id).description}
                            </Typography>
                          )}
                          
                          {/* Batch Information */}
                          {lineProduction?.batch_id && (
                            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32', mb: 1 }}>
                                Linked Batch: {batches.find(b => b.id === lineProduction.batch_id)?.batch_number || 'Unknown'}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                Total Cost: ${(() => {
                                  const batch = batches.find(b => b.id === lineProduction.batch_id);
                                  const batchCosts = batch?.batch_costs && batch.batch_costs.length > 0 ? batch.batch_costs[0] : null;
                                  const totalCost = batchCosts ? (batchCosts.total_production_batch_cost || batchCosts.total_cost) : (lineProduction.batch_total_material_cost || 0);
                                  return totalCost.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                                })()}
                              </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Per Unit: ${(lineProduction.batch_cost_per_unit || 0).toFixed(2)}
                                </Typography>
                              </Box>
                              {(() => {
                                const batch = batches.find(b => b.id === lineProduction.batch_id);
                                const batchCosts = batch?.batch_costs && batch.batch_costs.length > 0 ? batch.batch_costs[0] : null;
                                
                                if (batchCosts) {
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                                      Material: ${(batchCosts.total_ingredient_cost || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} | Labor: ${(batchCosts.labor_cost || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} | Overhead: ${(batchCosts.overhead_cost || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </Typography>
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Location & Schedule */}
                <Accordion 
                  expanded={accordionStates.locationSchedule}
                  onChange={(event, expanded) => updateAccordionState('locationSchedule', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: '12px !important',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Location & Schedule
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                          PRODUCTION LOCATION
                        </Typography>
                        <Chip 
                          icon={<FactoryIcon />}
                          label={lineProduction?.production_location ? getLocationLabel(lineProduction.production_location) : 'Not set'}
                          variant="outlined"
                          sx={{ 
                            justifyContent: 'flex-start', 
                            fontSize: '0.875rem', 
                            py: 1.5, 
                            px: 2,
                            width: '100%',
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.main,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiChip-icon': { 
                              color: theme.palette.primary.main,
                              mr: 1.5,
                              fontSize: '1.1rem'
                            },
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />
                      </Box>
                      
                      {(lineProduction?.manufacture_date || lineProduction?.expiration_date) && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                            KEY DATES
                          </Typography>
                          <Stack spacing={1}>
                            {lineProduction?.manufacture_date && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Manufacture:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDateForDisplay(lineProduction.manufacture_date)}
                                </Typography>
                              </Box>
                            )}
                            {lineProduction?.expiration_date && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Expires:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDateForDisplay(lineProduction.expiration_date)}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* PANEL B: Production Intelligence */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            {/* Production Summary Card */}
            <Card sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <InventoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                    Production Summary
                  </Typography>
                </Box>

                <Stack spacing={3}>
                  {/* Main Production Units */}
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 3, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 2, 
                    border: '1px solid #e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                    }
                  }}>
                    <Typography variant="h2" sx={{ fontWeight: 500, mb: 1, color: '#000000'}}>
                      {(() => {
                        // Calculate from unit configuration if available
                        if (lineProduction?.unit_configuration_id && lineProduction?.pallet_quantity !== undefined) {
                          const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                          if (unitConfig) {
                            let totalUnits = 0;
                            
                            // Calculate from stored pallet/case/unit quantities
                            if (unitConfig.enable_pallets && unitConfig.enable_cases && lineProduction.pallet_quantity > 0) {
                              const fullPallets = Math.floor(lineProduction.pallet_quantity);
                              totalUnits += fullPallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                            }
                            
                            if (unitConfig.enable_cases && lineProduction.case_quantity > 0) {
                              totalUnits += lineProduction.case_quantity * unitConfig.units_per_case;
                            }
                            
                            if (lineProduction.unit_quantity > 0) {
                              totalUnits += lineProduction.unit_quantity;
                            }
                            
                            return `${totalUnits.toLocaleString()} units`;
                          }
                        }
                        
                        // Fallback to variant distribution or planned quantity
                        if (lineProduction?.variant_distribution && lineProduction.variant_distribution.length > 0) {
                          return `${lineProduction.variant_distribution.reduce((sum, v) => sum + v.quantity, 0).toLocaleString()} units`;
                        }
                        
                        return `${(lineProduction?.planned_quantity || 0).toLocaleString()} units`;
                      })()}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                      Target Production Units
                    </Typography>
                    
                    {(() => {
                      // Show unit breakdown from configuration
                      if (lineProduction?.unit_configuration_id) {
                        const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                        const totalUnits = lineProduction.variant_distribution ? 
                          lineProduction.variant_distribution.reduce((sum, v) => sum + v.quantity, 0) : 
                          lineProduction.planned_quantity || 0;
                        
                        if (unitConfig && totalUnits > 0) {
                          let remainingUnits = totalUnits;
                          let pallets = 0;
                          let cases = 0;
                          let units = 0;
                          
                          // Calculate pallets if enabled
                          if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                            const unitsPerPallet = unitConfig.cases_per_pallet * unitConfig.units_per_case;
                            pallets = Math.floor(remainingUnits / unitsPerPallet);
                            remainingUnits = remainingUnits % unitsPerPallet;
                          }
                          
                          // Calculate cases if enabled
                          if (unitConfig.enable_cases) {
                            cases = Math.floor(remainingUnits / unitConfig.units_per_case);
                            remainingUnits = remainingUnits % unitConfig.units_per_case;
                          }
                          
                          // Remaining individual units
                          units = remainingUnits;
                          
                          // USE STORED VALUES DIRECTLY FOR DISPLAY
                          const storedPallets = lineProduction.pallet_quantity || 0;
                          const storedCases = lineProduction.case_quantity || 0;
                          const storedUnits = lineProduction.unit_quantity || 0;
                          
                          const parts = [];
                          if (storedPallets > 0) parts.push(`${storedPallets} pallets`);
                          if (storedCases > 0) parts.push(`${storedCases} cases`);
                          if (storedUnits > 0) parts.push(`${storedUnits} units`);
                          
                          // Calculate correct total from stored values
                          let correctTotal = 0;
                          if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                            correctTotal += storedPallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                          }
                          if (unitConfig.enable_cases) {
                            correctTotal += storedCases * unitConfig.units_per_case;
                          }
                          correctTotal += storedUnits;
                          
                          return (
                            <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                              {parts.join(' + ')} = {correctTotal.toLocaleString()} total units
                            </Typography>
                          );
                        }
                      }
                      
                      return (
                        <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                          {lineProduction?.variant_distribution ? 
                            `${lineProduction.variant_distribution.reduce((sum, v) => sum + v.quantity, 0).toLocaleString()} total units` :
                            `${(lineProduction?.planned_quantity || 0).toLocaleString()} total units`
                          }
                        </Typography>
                      );
                    })()}
                  </Box>

                  {/* Unit Configuration Summary */}
                  {lineProduction?.unit_configuration_id && (
                    <Box sx={{
                      p: 3,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                        Production Configuration
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          border: '1px solid rgba(0, 0, 0, 0.2)'
                        }
                      }}>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                          {(() => {
                            const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                            return unitConfig ? unitConfig.name : 'No Configuration';
                          })()}
                        </Typography>
                      </Box>

                      {/* Unit Breakdown Display */}
                      {lineProduction?.unit_configuration_id && (
                        <Box sx={{ 
                          mt: 3, 
                          pt: 3, 
                          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 2,
                          p: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                          }
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#000000' }}>
                            Unit Breakdown:
                          </Typography>
                          <Stack spacing={1}>
                            {(() => {
                              const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                              if (!unitConfig) return null;
                              
                              // USE THE STORED VALUES DIRECTLY
                              const pallets = lineProduction.pallet_quantity || 0;
                              const cases = lineProduction.case_quantity || 0;
                              const units = lineProduction.unit_quantity || 0;
                              
                              // Calculate total from stored values
                              let totalUnits = 0;
                              if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                              }
                              if (unitConfig.enable_cases) {
                                totalUnits += cases * unitConfig.units_per_case;
                              }
                              totalUnits += units;
                              
                              return (
                                <>
                                  {pallets > 0 && (
                                    <>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Pallets:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                          {pallets} {unitConfig.pallet_unit_name || 'pallets'}
                                        </Typography>
                                      </Box>
                                      {unitConfig.enable_cases && unitConfig.cases_per_pallet > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2 }}>
                                          <Typography variant="body2" color="text.secondary">
                                            = Cases from pallets:
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {(pallets * unitConfig.cases_per_pallet).toLocaleString()} {unitConfig.case_unit_name || 'cases'}
                                          </Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  {cases > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Cases:</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                        {cases} cases
                                      </Typography>
                                    </Box>
                                  )}
                                  {units > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Individual Units:</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                        {units} units
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ 
                                    mt: 2, 
                                    pt: 2, 
                                    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: 1,
                                    p: 1
                                  }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Total Units:</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                      {totalUnits.toLocaleString()} units
                                    </Typography>
                                  </Box>
                                </>
                              );
                            })()}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Template Details Card */}
            {lineProduction?.template_id && (
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <PrecisionManufacturingIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                    Production Template
                  </Typography>
                  
                  {/* Batch Cost Display */}
                  {lineProduction?.batch_id && (
                    <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                        ${(() => {
                          const batch = batches.find(b => b.id === lineProduction.batch_id);
                          const batchCosts = batch?.batch_costs && batch.batch_costs.length > 0 ? batch.batch_costs[0] : null;
                          const totalCost = batchCosts ? (batchCosts.total_production_batch_cost || batchCosts.total_cost) : (lineProduction.batch_total_material_cost || 0);
                          return totalCost.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                        })()} batch cost
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${(lineProduction.batch_cost_per_unit || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} per unit
                      </Typography>
                    </Box>
                  )}
                </Box>
                  
                  {(() => {
                    const template = templates.find(t => t.id === lineProduction.template_id);
                    return (
                      <Box>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                            {template?.template_name || 'Unknown Template'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {template?.description || 'No description available'}
                          </Typography>
                        </Box>
                        
                        {template?.product?.name && (
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                          }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Template Product: <strong>{template.product.name}</strong>
                            </Typography>
                          </Box>
                        )}

                        {/* Template Breakdown */}
                        {lineProduction?.variant_distribution && lineProduction.variant_distribution.length > 0 && templateConsumption && (
                          <Accordion 
                            expanded={accordionStates.templateBreakdown}
                            onChange={(event, expanded) => updateAccordionState('templateBreakdown', expanded)}
                            elevation={0}
                            sx={{
                              mt: 3,
                              mb: 3,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                              backdropFilter: 'blur(20px)',
                              overflow: 'hidden',
                              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                              '&:before': {
                                display: 'none'
                              },
                              '&.Mui-expanded': {
                                margin: '24px 0 24px 0'
                              },
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                          >
                            <AccordionSummary 
                              expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                              sx={{
                                borderRadius: '8px 8px 0 0',
                                background: 'white',
                                backdropFilter: 'blur(10px)',
                                border: 'none',
                                minHeight: 64,
                                '&.Mui-expanded': {
                                  minHeight: 64,
                                  borderRadius: '8px 8px 0 0'
                                },
                                '& .MuiAccordionSummary-content': {
                                  margin: '16px 0',
                                  alignItems: 'center',
                                  '&.Mui-expanded': {
                                    margin: '16px 0'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ 
                                  p: 1.5, 
                                  borderRadius: 2, 
                                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  backdropFilter: 'blur(10px)',
                                  mr: 2
                                }}>
                                  <PrecisionManufacturingIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                    Template Component Breakdown
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Specific components needed for {(() => {
                                      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                                      if (!unitConfig) return 0;
                                      
                                      const pallets = lineProduction.pallet_quantity || 0;
                                      const cases = lineProduction.case_quantity || 0;
                                      const units = lineProduction.unit_quantity || 0;
                                      
                                      let total = 0;
                                      if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                        total += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                                      }
                                      if (unitConfig.enable_cases) {
                                        total += cases * unitConfig.units_per_case;
                                      }
                                      total += units;
                                      
                                      return total;
                                    })()} units
                                  </Typography>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 4 }}>

                            {/* Show message if template has no components */}
                            {templateConsumption.bottles.length === 0 && 
                             templateConsumption.caps.length === 0 && 
                             templateConsumption.labels.length === 0 && 
                             templateConsumption.supplies.length === 0 && (
                              <Box sx={{ 
                                textAlign: 'center', 
                                py: 6,
                                backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                borderRadius: 2,
                                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`
                              }}>
                                <Typography variant="h6" sx={{ mb: 1, color: theme.palette.warning.main, fontWeight: 500 }}>
                                  No Components Configured
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  This template doesn't have any bottles, caps, labels, or supplies configured.
                                  <br />
                                  Edit the template to add component consumption details.
                                </Typography>
                              </Box>
                            )}

                            {/* Bottles */}
                            {templateConsumption.bottles.length > 0 && (
                              <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                                  Bottles ({templateConsumption.bottles.length} types)
                                </Typography>
                                <Stack spacing={2}>
                                  {templateConsumption.bottles.map((bottle, index) => {
                                    const totalNeeded = (() => {
                                      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                                      if (!unitConfig) return 0;
                                      
                                      const pallets = lineProduction.pallet_quantity || 0;
                                      const cases = lineProduction.case_quantity || 0;
                                      const units = lineProduction.unit_quantity || 0;
                                      
                                      let totalUnits = 0;
                                      if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                        totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                                      }
                                      if (unitConfig.enable_cases) {
                                        totalUnits += cases * unitConfig.units_per_case;
                                      }
                                      totalUnits += units;
                                      
                                      return totalUnits * bottle.quantity_per_unit;
                                    })();
                                    return (
                                      <Box key={index} sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 3,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          border: '1px solid rgba(0, 0, 0, 0.2)'
                                        }
                                      }}>
                                        <Box>
                                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            {bottle.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {bottle.size} {bottle.material} • {bottle.quantity_per_unit}/unit
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                          {totalNeeded.toLocaleString()} bottles
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}

                            {/* Caps */}
                            {templateConsumption.caps.length > 0 && (
                              <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                                  Caps ({templateConsumption.caps.length} types)
                                </Typography>
                                <Stack spacing={2}>
                                  {templateConsumption.caps.map((cap, index) => {
                                    const totalNeeded = (() => {
                                      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                                      if (!unitConfig) return 0;
                                      
                                      const pallets = lineProduction.pallet_quantity || 0;
                                      const cases = lineProduction.case_quantity || 0;
                                      const units = lineProduction.unit_quantity || 0;
                                      
                                      let totalUnits = 0;
                                      if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                        totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                                      }
                                      if (unitConfig.enable_cases) {
                                        totalUnits += cases * unitConfig.units_per_case;
                                      }
                                      totalUnits += units;
                                      
                                      return totalUnits * cap.quantity_per_unit;
                                    })();
                                    return (
                                      <Box key={index} sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 3,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          border: '1px solid rgba(0, 0, 0, 0.2)'
                                        }
                                      }}>
                                        <Box>
                                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            {cap.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {cap.size} {cap.material} • {cap.quantity_per_unit}/unit
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                          {totalNeeded.toLocaleString()} caps
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}

                            {/* Labels */}
                            {templateConsumption.labels.length > 0 && (
                              <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                                  Labels ({templateConsumption.labels.length} types)
                                </Typography>
                                <Stack spacing={2}>
                                  {templateConsumption.labels.map((label, index) => {
                                    const totalNeeded = (() => {
                                      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                                      if (!unitConfig) return 0;
                                      
                                      const pallets = lineProduction.pallet_quantity || 0;
                                      const cases = lineProduction.case_quantity || 0;
                                      const units = lineProduction.unit_quantity || 0;
                                      
                                      let totalUnits = 0;
                                      if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                        totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                                      }
                                      if (unitConfig.enable_cases) {
                                        totalUnits += cases * unitConfig.units_per_case;
                                      }
                                      totalUnits += units;
                                      
                                      return totalUnits * label.quantity_per_unit;
                                    })();
                                    return (
                                      <Box key={index} sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 3,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          border: '1px solid rgba(0, 0, 0, 0.2)'
                                        }
                                      }}>
                                        <Box>
                                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            {label.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {label.size} {label.material} • {label.quantity_per_unit}/unit
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                          {totalNeeded.toLocaleString()} labels
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}

                            {/* Supplies */}
                            {templateConsumption.supplies.length > 0 && (
                              <Box sx={{ mb: 0 }}>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                                  Supplies ({templateConsumption.supplies.length} types)
                                </Typography>
                                <Stack spacing={2}>
                                  {(showAllSupplies ? templateConsumption.supplies : templateConsumption.supplies.slice(0, 5)).map((supply, index) => {
                                    const totalNeeded = (() => {
                                      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
                                      if (!unitConfig) return 0;
                                      
                                      const pallets = lineProduction.pallet_quantity || 0;
                                      const cases = lineProduction.case_quantity || 0;
                                      const units = lineProduction.unit_quantity || 0;
                                      
                                      let totalUnits = 0;
                                      if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                                        totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
                                      }
                                      if (unitConfig.enable_cases) {
                                        totalUnits += cases * unitConfig.units_per_case;
                                      }
                                      totalUnits += units;
                                      
                                      return totalUnits * supply.quantity_per_unit;
                                    })();
                                    return (
                                      <Box key={index} sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 3,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          border: '1px solid rgba(0, 0, 0, 0.2)'
                                        }
                                      }}>
                                        <Box>
                                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            {supply.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {supply.category} • {supply.quantity_per_unit}/unit
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                          {totalNeeded.toLocaleString()} {supply.unit_of_measure || 'units'}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                                
                                {/* Show More/Less Button */}
                                {templateConsumption.supplies.length > 5 && (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    mt: 3 
                                  }}>
                                    <Button
                                      variant="outlined"
                                      onClick={() => setShowAllSupplies(!showAllSupplies)}
                                      sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        fontWeight: 500,
                                        border: '1px solid #e0e0e0',
                                        color: '#000000',
                                        backgroundColor: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          border: '1px solid rgba(0, 0, 0, 0.2)',
                                          backgroundColor: '#f8faff'
                                        }
                                      }}
                                    >
                                      {showAllSupplies 
                                        ? `Show Less (${templateConsumption.supplies.length - 5} hidden)` 
                                        : `Show All ${templateConsumption.supplies.length} Supplies (${templateConsumption.supplies.length - 5} more)`
                                      }
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Show message if no template consumption data */}
                            {(!templateConsumption.bottles.length && !templateConsumption.caps.length && 
                              !templateConsumption.labels.length && !templateConsumption.supplies.length) && (
                              <Box sx={{ 
                                textAlign: 'center', 
                                p: 3, 
                                backgroundColor: 'rgba(255,193,7,0.1)', 
                                borderRadius: 1,
                                border: '1px solid rgba(255,193,7,0.3)'
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No component consumption data found for this template.
                                  <br />
                                  Configure the template to show detailed breakdown.
                                </Typography>
                              </Box>
                            )}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Inventory Analysis Card */}
            {inventoryAnalysis && (
              <Card sx={{ 
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <InventoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      ml: 2
                    }}>
                      Inventory Analysis
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500, 
                      mb: 1,
                      color: theme.palette.text.primary
                    }}>
                      {inventoryAnalysis.shortages.length === 0 ? 'All Components Available' : 'Component Shortages Detected'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Maximum possible units:
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      }}>
                        {inventoryAnalysis.canMakeUnits.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Component Analysis */}
                  {inventoryAnalysis.inventoryItems.length > 0 ? (
                  <Accordion 
                    expanded={accordionStates.componentAvailability}
                    onChange={(event, expanded) => updateAccordionState('componentAvailability', expanded)}
                    elevation={0}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        borderRadius: '8px 8px 0 0',
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        minHeight: 56,
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderRadius: '8px 8px 0 0'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                          '&.Mui-expanded': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Component Availability ({inventoryAnalysis.inventoryItems.length} items)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <Stack spacing={1}>
                      {inventoryAnalysis.inventoryItems.map((item, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          background: item.canMake ? 
                            `linear-gradient(${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})` : 
                            `linear-gradient(${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
                          borderRadius: 2,
                          border: `1px solid ${item.canMake ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2)}`,
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 2px 8px ${item.canMake ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1)}`,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 16px ${item.canMake ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15)}`,
                            border: `1px solid ${item.canMake ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.4)}`
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, fontSize: '0.8rem' }}>
                              {item.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Need: {item.needed.toLocaleString()} units
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500, 
                              color: item.canMake ? theme.palette.success.main : theme.palette.error.main,
                              fontSize: '0.8rem'
                            }}>
                              {item.canMake ? 
                                `✓ ${item.available.toLocaleString()} units` :
                                `✗ ${item.available.toLocaleString()} units`
                              }
                            </Typography>
                            {item.shortage > 0 && (
                              <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
                                Short: {item.shortage.toLocaleString()} units
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      mb: 3,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                      textAlign: 'center'
                    }}>
                      <InventoryIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                      <Typography variant="body2" sx={{ color: theme.palette.info.main, fontWeight: 500 }}>
                        No component analysis required
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This template doesn't require any bottles, caps, labels, or supplies.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ 
                    mt: 2,
                    p: 3, 
                    background: `linear-gradient(${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                      Recommendation:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {inventoryAnalysis.shortages.length === 0 ? 
                        'All components are available. Production can proceed as planned.' :
                        `Order the missing components above to complete production of ${inventoryAnalysis.totalUnitsNeeded} units.`
                      }
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* PANEL C: Line Production Status */}
        <Grid item xs={12} lg={3}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <FactoryIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Line Status
                </Typography>
              </Box>
              
              <Stack spacing={3}>
                {/* Current Status Display */}
                <Box sx={{ 
                  p: 3,
                  border: lineProduction?.status === 'completed' 
                    ? `1px solid ${alpha(theme.palette.grey[400], 0.4)}`
                    : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                  textAlign: 'center',
                  background: lineProduction?.status === 'completed' 
                    ? alpha(theme.palette.grey[100], 0.7)
                    : `white`,
                  backdropFilter: 'blur(10px)',
                  boxShadow: lineProduction?.status === 'completed'
                    ? `0 2px 8px ${alpha(theme.palette.grey[400], 0.1)}`
                    : `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                  opacity: lineProduction?.status === 'completed' ? 0.8 : 1,
                  '&:hover': lineProduction?.status !== 'completed' ? {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  } : {},
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: lineProduction?.status === 'completed' 
                      ? alpha(theme.palette.text.secondary, 0.7)
                      : theme.palette.text.primary, 
                    mb: 1 
                  }}>
                    {formatStatus(lineProduction?.status)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontWeight: 500,
                    opacity: lineProduction?.status === 'completed' ? 0.7 : 1
                  }}>
                    Current line status
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary, mt: 1 }}>
                    Priority: {lineProduction?.priority?.toUpperCase() || 'NORMAL'}
                  </Typography>
                </Box>

                {/* Production Details */}
                {(lineProduction?.production_line || lineProduction?.shift_supervisor) && (
                  <Accordion 
                    expanded={accordionStates.productionDetails}
                    onChange={(event, expanded) => updateAccordionState('productionDetails', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        borderRadius: '8px 8px 0 0',
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        minHeight: 56,
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderRadius: '8px 8px 0 0'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                          '&.Mui-expanded': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Production Details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        {lineProduction?.production_line && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Production Line:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                              {lineProduction.production_line}
                            </Typography>
                          </Box>
                        )}
                        {lineProduction?.shift_supervisor && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Supervisor:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                              {lineProduction.shift_supervisor}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Production Notes */}
                {lineProduction?.production_notes && (
                  <Accordion 
                    expanded={accordionStates.productionNotes}
                    onChange={(event, expanded) => updateAccordionState('productionNotes', expanded)}
                    elevation={0}
                    sx={{
                      mt: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '24px 0 24px 0'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        borderRadius: '8px 8px 0 0',
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        minHeight: 56,
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderRadius: '8px 8px 0 0'
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'center',
                          '&.Mui-expanded': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        Production Notes
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {lineProduction.production_notes}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                
                {/* Timeline */}
                <Accordion 
                  expanded={accordionStates.timeline}
                  onChange={(event, expanded) => updateAccordionState('timeline', expanded)}
                  elevation={0}
                  sx={{
                    mt: 3,
                    mb: 3,
                    borderRadius: '12px !important',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '24px 0 24px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      minHeight: 56,
                      '&.Mui-expanded': {
                        minHeight: 56
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Timeline
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Created:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {lineProduction?.created_at ? new Date(lineProduction.created_at).toLocaleDateString() : 'Not set'}
                        </Typography>
                      </Box>
                      {lineProduction?.updated_at && lineProduction?.updated_at !== lineProduction?.created_at && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Updated:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(lineProduction.updated_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      {lineProduction?.planned_start_date && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Planned Start:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateForDisplay(lineProduction.planned_start_date)}
                            {lineProduction?.planned_start_time && ` at ${lineProductionTimeFormat === '24hour' ? lineProduction.planned_start_time : dayjs(`2000-01-01 ${lineProduction.planned_start_time}`).format('h:mm A')}`}
                          </Typography>
                        </Box>
                      )}
                      {lineProduction?.planned_completion_date && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Planned Completion:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateForDisplay(lineProduction.planned_completion_date)}
                            {lineProduction?.planned_completion_time && ` at ${lineProductionTimeFormat === '24hour' ? lineProduction.planned_completion_time : dayjs(`2000-01-01 ${lineProduction.planned_completion_time}`).format('h:mm A')}`}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCancel(); }}
        disableEscapeKeyDown
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'white',
              color: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FactoryIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 0.5 }}>
                {isCreatingNew ? 'Create New Line Production' : 'Edit Line Production'}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                Configure line production parameters and details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        {validationError && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                fontWeight: 500,
                '& .MuiAlert-message': {
                  whiteSpace: 'pre-line'
                }
              }}
            >
              {validationError}
            </Alert>
          </Box>
        )}
        
        <DialogContent sx={{ backgroundColor: '#f8fafc', p: 4, mt: 2, maxHeight: '80vh', overflow: 'auto'}}>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            {/* Step 1: Basic Information */}
            <Step>
              <StepLabel 
                onClick={() => handleStep(0)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Basic Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Batch linking, identifiers and status configuration
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  {/* Batch Production Linking */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <LocalDrinkIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                    Batch Production Linking
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Link to production batch to auto-populate batch info, product details, and expiration date
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {getMatchingBatches().length > 0 && (
              <Grid item xs={12}>
                <Alert 
                  severity="info" 
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    mb: 2
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    Matching Batches Found!
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Found {getMatchingBatches().length} batch{getMatchingBatches().length === 1 ? '' : 'es'} with matching batch/lot numbers. Select one to auto-populate fields:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {getMatchingBatches().map((batch) => (
                      <Button
                        key={batch.id}
                        size="small"
                        variant="outlined"
                        onClick={() => handleBatchSelect(batch)}
                        sx={{
                          borderRadius: 2,
                      fontWeight: 500,
                          fontSize: '0.8rem',
                          mb: 1,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          color: theme.palette.info.main,
                          '&:hover': {
                            borderColor: theme.palette.info.main,
                            backgroundColor: alpha(theme.palette.info.main, 0.04)
                          }
                        }}
                      >
                        {batch.batch_number}{batch.lot_number ? ` (${batch.lot_number})` : ''}
                      </Button>
                    ))}
                  </Stack>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenBatchSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <LocalDrinkIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.batch_id ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {batches.find(b => b.id === editData.batch_id)?.batch_number || 'Unknown Batch'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const batch = batches.find(b => b.id === editData.batch_id);
                            if (!batch) return 'No details';
                            
                            const batchCosts = batch.batch_costs && batch.batch_costs.length > 0 ? batch.batch_costs[0] : null;
                            const totalCost = batchCosts ? (batchCosts.total_production_batch_cost || batchCosts.total_cost) : (batch.actual_material_cost || batch.planned_material_cost || 0);
                            
                            return `Cost: $${totalCost.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} • ${batch.product?.name || 'No Product'}`;
                          })()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                        Link Production Batch (Recommended)
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
            </Grid>

            {editData.batch_id && (
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  backgroundColor: '#e8f5e8',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                    borderColor: '#388e3c'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalDrinkIcon sx={{ color: '#2e7d32', fontSize: 32, mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                      Linked Batch Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        backgroundColor: 'rgba(255,255,255,0.8)', 
                        borderRadius: 2, 
                        border: '1px solid rgba(0,0,0,0.1)',
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                          TOTAL COST
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                          ${(editData.batch_total_material_cost || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Full Cost
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        backgroundColor: 'rgba(255,255,255,0.8)', 
                        borderRadius: 2, 
                        border: '1px solid rgba(0,0,0,0.1)',
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                          COST PER UNIT
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                          ${(editData.batch_cost_per_unit || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          per unit
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        backgroundColor: 'rgba(255,255,255,0.8)', 
                        borderRadius: 2, 
                        border: '1px solid rgba(0,0,0,0.1)',
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                          BATCH STATUS
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                          {(() => {
                            const batch = batches.find(b => b.id === editData.batch_id);
                            return batch?.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
                          })()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        backgroundColor: 'rgba(255,255,255,0.8)', 
                        borderRadius: 2, 
                        border: '1px solid rgba(0,0,0,0.1)',
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                          BATCH SIZE
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                          {(() => {
                            const batch = batches.find(b => b.id === editData.batch_id);
                            if (!batch) return '0 units';
                            
                            return `${batch.planned_quantity || 0} ${batch.planned_unit_of_measure || 'units'}`;
                          })()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            )}

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Batch Number *"
                          value={editData.batch_number || ''}
                          onChange={(e) => setEditData({ ...editData, batch_number: e.target.value })}
                          required
                          error={!editData.batch_number?.trim()}
                          helperText={!editData.batch_number?.trim() ? 'Batch number is required' : ''}
                          FormHelperTextProps={{
                            sx: { color: '#000000' }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)'
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Lot Number *"
                          value={editData.lot_number || ''}
                          onChange={(e) => setEditData({ ...editData, lot_number: e.target.value })}
                          required
                          error={!editData.lot_number?.trim()}
                          helperText={!editData.lot_number?.trim() ? 'Lot number is required' : ''}
                          FormHelperTextProps={{
                            sx: { color: '#000000' }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)'
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Internal Code (Optional)"
                          value={editData.internal_code || ''}
                          onChange={(e) => setEditData({ ...editData, internal_code: e.target.value })}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)'
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required error={!editData.status}>
                          <InputLabel>Status *</InputLabel>
                          <Select
                            value={editData.status || ''}
                            onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                            label="Status *"
                            sx={{
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="pending_approval">Pending Approval</MenuItem>
                            <MenuItem value="scheduled">Scheduled</MenuItem>
                            <MenuItem value="in_production">In Production</MenuItem>
                            <MenuItem value="quality_testing">Quality Testing</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="on_hold">On Hold</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                          </Select>
                          {!editData.status && (
                            <Typography variant="caption" sx={{ color: '#000000', mt: 0.5, fontSize: '0.75rem', ml: 2 }}>
                              Status is required
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 2: Product Selection */}
            <Step>
              <StepLabel
                onClick={() => handleStep(1)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Product Selection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the product to produce
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenProductSelection}
                          sx={{
                            height: 56,
                            borderRadius: 3,
                            border: !editData.product_id ? 
                              `1px solid ${theme.palette.error.main}` : 
                              `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            justifyContent: 'flex-start',
                            px: 3,
                            py: 2.5,
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <InventoryIcon sx={{ mr: 2, color: 'text.secondary' }} />
                            <Box sx={{ textAlign: 'left', flex: 1 }}>
                              {editData.product_id ? (
                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                  {products.find(p => p.id === editData.product_id)?.name || 'Unknown Product'}
                                </Typography>
                              ) : (
                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'error.main' }}>
                                  Select Product to Make *
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Button>
                        {!editData.product_id && (
                          <Typography variant="caption" sx={{ color: '#000000', mt: 0.5, fontSize: '0.75rem', ml: 2 }}>
                            Product selection is required
                          </Typography>
                        )}
                      </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 3: Unit Configuration */}
            <Step>
              <StepLabel
                onClick={() => handleStep(2)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Unit Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure packaging structure and quantities
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                      {editData.product_id ? (
                        <>
                          {/* Unit Configuration Section */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  backdropFilter: 'blur(10px)',
                                  color: theme.palette.primary.main,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 2
                                }}>
                                  <SettingsIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                                    Unit Configuration
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Select packaging structure first
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setUnitConfigDialogOpen(true)}
                                sx={{
                                  borderRadius: 2,
                                  px: 3,
                                  py: 1.2,
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  bgcolor: 'primary.main',
                                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                                  '&:hover': {
                                    bgcolor: 'primary.dark',
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                  }
                                }}
                              >
                                Add Configuration
                              </Button>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            {unitConfigurations.length === 0 ? (
                              <Box sx={{
                                textAlign: 'center',
                                py: 6,
                                px: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.grey[50], 0.5)
                              }}>
                                <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                                  No Unit Configurations Found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                                  Create unit configurations to define packaging structures.
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                {selectedUnitConfig ? (
                                  <Card sx={{ 
                                    borderRadius: 3, 
                                    border: `2px solid ${theme.palette.primary.main}`,
                                    background: alpha(theme.palette.primary.main, 0.05),
                                    p: 3,
                                    mb: 2
                                  }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem', mb: 1 }}>
                                          {selectedUnitConfig.name}
                                        </Typography>
                                        
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem' }}>
                                          {selectedUnitConfig.enable_cases && !selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} → ${selectedUnitConfig.case_unit_name}`}
                                          {selectedUnitConfig.enable_cases && selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} → ${selectedUnitConfig.case_unit_name} → ${selectedUnitConfig.pallet_unit_name}`}
                                          {!selectedUnitConfig.enable_cases && !selectedUnitConfig.enable_pallets && `${selectedUnitConfig.base_unit_name} only`}
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                          {selectedUnitConfig.enable_cases && (
                                            <Chip 
                                              label="Cases" 
                                              size="small" 
                                              sx={{
                                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                                color: 'info.main',
                                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                height: 24
                                              }}
                                            />
                                          )}
                                          {selectedUnitConfig.enable_pallets && (
                                            <Chip 
                                              label="Pallets" 
                                              size="small" 
                                              sx={{
                                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                                color: 'success.main',
                                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                height: 24
                                              }}
                                            />
                                          )}
                                        </Box>

                                        <Grid container spacing={2}>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                              Units/Case
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                              {selectedUnitConfig.enable_cases ? selectedUnitConfig.units_per_case : 'N/A'}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                              Cases/Pallet
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                              {selectedUnitConfig.enable_pallets ? selectedUnitConfig.cases_per_pallet : 'N/A'}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                              Total/Pallet
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                              {selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases
                                                ? (selectedUnitConfig.units_per_case * selectedUnitConfig.cases_per_pallet).toLocaleString()
                                                : selectedUnitConfig.enable_cases
                                                ? `${selectedUnitConfig.units_per_case}/case`
                                                : '1'
                                              }
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleOpenUnitConfigSelection}
                                        sx={{
                                          ml: 2,
                                          borderRadius: 2,
                                          textTransform: 'none',
                                          fontWeight: 500,
                                          fontSize: '0.85rem'
                                        }}
                                      >
                                        Change
                                      </Button>
                                    </Box>
                                  </Card>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleOpenUnitConfigSelection}
                                    sx={{
                                      py: 3,
                                      borderRadius: 3,
                                      border: `2px dashed ${alpha(theme.palette.grey[400], 0.8)}`,
                                      textTransform: 'none',
                                      fontWeight: 500,
                                      fontSize: '1rem',
                                      color: 'text.secondary',
                                      '&:hover': {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        color: 'primary.main'
                                      }
                                    }}
                                  >
                                    <SettingsIcon sx={{ mr: 1 }} />
                                    Select Unit Configuration
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Grid>

                          {selectedUnitConfig ? (
                            <>
                              {/* Advanced Unit Configuration for variants with quantities */}
                              {editData.product_variant_id && (
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                      backdropFilter: 'blur(10px)',
                                      color: theme.palette.primary.main,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      mr: 2
                                    }}>
                                      <SettingsIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                                        Advanced Packaging Configuration
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        Configure pallets, cases, and units for variants being produced
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {editData.product_variant_id && productVariants.filter(variant => variant.id === editData.product_variant_id).map((variant, index) => (
                                  <Grid item xs={12} key={variant.id}>
                                    <Card sx={{ 
                                      p: 4, 
                                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                      borderRadius: 3,
                                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)}, ${alpha(theme.palette.success.main, 0.02)})`,
                                      backdropFilter: 'blur(20px)',
                                      transition: 'all 0.3s ease',
                                      boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                                        borderColor: theme.palette.success.main
                                      }
                                    }}>
                                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                                        {variant.name || 'Variant'} Packaging Configuration
                                        {variant.sku && (
                                          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                                            (SKU: {variant.sku})
                                          </Typography>
                                        )}
                                      </Typography>

                                      <Grid container spacing={3}>
                                        {selectedUnitConfig.enable_pallets && (
                                          <Grid item xs={12} sm={4}>
                                          <TextField
                                            fullWidth
                                            label={`${(selectedUnitConfig.pallet_unit_name || 'Pallets').charAt(0).toUpperCase() + (selectedUnitConfig.pallet_unit_name || 'pallets').slice(1)} to Make`}
                                            type="number"
                                            value={unitInputs[variant.id]?.pallets || ''}
                                            onChange={(e) => {
                                              const newPallets = parseInt(e.target.value) || 0;
                                              const newInputs = { ...(unitInputs[variant.id] || {}), pallets: newPallets };
                                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                              updateVariantFromUnitInputs(variant.id, newInputs);
                                            }}
                                            inputProps={{ min: 0, step: 1 }}
                                            placeholder="0"
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  borderRadius: 2,
                                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                  backdropFilter: 'blur(10px)',
                                                  '& fieldset': {
                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                    borderWidth: 1
                                                  },
                                                  '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                  },
                                                  '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                  }
                                                }
                                              }}
                                            />
                                          </Grid>
                                        )}

                                        {selectedUnitConfig.enable_cases && (
                                          <Grid item xs={12} sm={4}>
                                          <TextField
                                            fullWidth
                                            label={`${(selectedUnitConfig.case_unit_name || 'Cases').charAt(0).toUpperCase() + (selectedUnitConfig.case_unit_name || 'cases').slice(1)} to Make`}
                                            type="number"
                                            value={unitInputs[variant.id]?.cases || ''}
                                            onChange={(e) => {
                                              const newCases = parseInt(e.target.value) || 0;
                                              const newInputs = { ...(unitInputs[variant.id] || {}), cases: newCases };
                                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                              updateVariantFromUnitInputs(variant.id, newInputs);
                                            }}
                                            inputProps={{ min: 0, step: 1 }}
                                            placeholder="0"
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  borderRadius: 2,
                                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                  backdropFilter: 'blur(10px)',
                                                  '& fieldset': {
                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                    borderWidth: 1
                                                  },
                                                  '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                  },
                                                  '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.primary.main
                                                  }
                                                }
                                              }}
                                            />
                                          </Grid>
                                        )}

                                        <Grid item xs={12} sm={4}>
                                          <TextField
                                            fullWidth
                                            label={`${(selectedUnitConfig.base_unit_name || 'Units').charAt(0).toUpperCase() + (selectedUnitConfig.base_unit_name || 'units').slice(1)} to Make`}
                                            type="number"
                                            value={unitInputs[variant.id]?.units || ''}
                                            onChange={(e) => {
                                              const newUnits = parseInt(e.target.value) || 0;
                                              const newInputs = { ...(unitInputs[variant.id] || {}), units: newUnits };
                                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                                              updateVariantFromUnitInputs(variant.id, newInputs);
                                            }}
                                            inputProps={{ min: 0, step: 1 }}
                                            placeholder="0"
                                            sx={{
                                              '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                backdropFilter: 'blur(10px)',
                                                '& fieldset': {
                                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                                  borderWidth: 1
                                                },
                                                '&:hover fieldset': {
                                                  borderColor: theme.palette.primary.main
                                                },
                                                '&.Mui-focused fieldset': {
                                                  borderColor: theme.palette.primary.main
                                                }
                                              }
                                            }}
                                          />
                                        </Grid>

                                        <Grid item xs={12}>
                                          <Box sx={{ 
                                            backgroundColor: '#f8fafc',
                                            borderRadius: 2,
                                            p: 3,
                                            border: '1px solid #e0e0e0',
                                            textAlign: 'center'
                                          }}>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                                              Total: {calculateTotalUnitsFromInputs(variant.id, unitInputs[variant.id]).toLocaleString()} {selectedUnitConfig.base_unit_name || 'units'}
                                            </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Total Production Units: {calculateTotalUnitsFromInputs(variant.id, unitInputs[variant.id]).toLocaleString()} units
                              </Typography>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Card>
                                  </Grid>
                                ))}
                            </>
                          ) : !selectedUnitConfig ? (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                Please select a unit configuration first.
                              </Typography>
                            </Grid>
                          ) : productVariants.length === 0 ? (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                No variants available for this product. Please add variants first.
                              </Typography>
                            </Grid>
                          ) : null}

                          <Grid item xs={12}>
                            <Card sx={{ 
                              p: 3, 
                              backgroundColor: '#e8f5e8',
                              borderRadius: 3,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                              boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.08)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                                borderColor: '#388e3c'
                              }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <FactoryIcon sx={{ color: '#2e7d32', fontSize: 32, mr: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                  Total Production
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ fontWeight: 500, color: '#2e7d32', mb: 1 }}>
                                {(() => {
                                  if (!selectedUnitConfig || !editData.product_variant_id) return 0;
                                  return calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]).toLocaleString();
                                })()} units
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {selectedUnitConfig && editData.product_variant_id ? 
                                  `Total Units: ${calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]).toLocaleString()} units` :
                                  'No unit configuration set'
                                }
                              </Typography>
                            </Card>
                          </Grid>
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                            Please select a product first (Step 2).
                          </Typography>
                        </Grid>
                      )}
                </Grid>
              </StepContent>
            </Step>

            {/* Step 4: Location & Template */}
            <Step>
              <StepLabel
                onClick={() => handleStep(3)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Production Location & Template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select production facility and line template
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenLocationSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: !editData.production_location ? 
                    `1px solid ${theme.palette.error.main}` : 
                    `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <BusinessIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.production_location ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {locations.find(l => l.id === editData.production_location)?.name || 'Unknown Location'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const location = locations.find(l => l.id === editData.production_location);
                            return location ? `${location.facility_type} • ${location.city}, ${location.state}` : 'No details';
                          })()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'error.main' }}>
                        Select Production Location *
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
              {!editData.production_location && (
                <Typography variant="caption" sx={{ color: '#000000', mt: 0.5, fontSize: '0.75rem', ml: 2 }}>
                  Production location is required
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenTemplateSelection}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: !editData.template_id ? 
                    `1px solid ${theme.palette.error.main}` : 
                    `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <PrecisionManufacturingIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {editData.template_id ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {templates.find(t => t.id === editData.template_id)?.template_name || 'Unknown Template'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const template = templates.find(t => t.id === editData.template_id);
                            return template ? `${template.product?.name || 'No Product'} • ${template.description || 'No Description'}` : 'No details';
                          })()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'error.main' }}>
                        Select Production Template *
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Button>
              {!editData.template_id && (
                <Typography variant="caption" sx={{ color: '#000000', mt: 0.5, fontSize: '0.75rem', ml: 2 }}>
                  Production template is required
                </Typography>
              )}
              {editData.template_id && 
               templateConsumption.bottles.length === 0 && 
               templateConsumption.caps.length === 0 && 
               templateConsumption.labels.length === 0 && 
               templateConsumption.supplies.length === 0 && (
                <Box sx={{ 
                  mt: 1.5, 
                  ml: 2,
                  p: 1.5, 
                  backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1
                }}>
                  <WarningIcon sx={{ fontSize: 16, color: theme.palette.warning.main, mt: 0.2 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 500, display: 'block' }}>
                      Template has no components configured
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      This template doesn't have any bottles, caps, labels, or supplies configured. You may need to edit the template to add components.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 5: Dates & Schedule */}
            <Step>
              <StepLabel
                onClick={() => handleStep(4)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Dates & Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set manufacturing timeline and deadlines
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <TimelineIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                      Dates & Schedule
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Production timeline and key milestone dates
                    </Typography>
                  </Box>
                </Box>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Format</InputLabel>
                  <Select
                    value={lineProductionTimeFormat}
                    onChange={(e) => updateLineProductionTimeFormat(e.target.value)}
                    label="Time Format"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          borderWidth: 1
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <MenuItem value="12hour">12 Hour</MenuItem>
                    <MenuItem value="24hour">24 Hour</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Manufacture Date *"
                  value={editData.manufacture_date ? dayjs(editData.manufacture_date, 'YYYY-MM-DD') : null}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      manufacture_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !editData.manufacture_date,
                      helperText: !editData.manufacture_date ? 'Manufacture date is required' : '',
                      FormHelperTextProps: {
                        sx: { color: '#000000' }
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiration Date *"
                  value={editData.expiration_date ? dayjs(editData.expiration_date, 'YYYY-MM-DD') : null}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      expiration_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !editData.expiration_date,
                      helperText: !editData.expiration_date ? 'Expiration date is required' : '',
                      FormHelperTextProps: {
                        sx: { color: '#000000' }
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Planned Start Date & Time *"
                  value={editData.planned_start_datetime}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      planned_start_datetime: newValue
                    });
                  }}
                  format={lineProductionTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                  ampm={lineProductionTimeFormat !== '24hour'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !editData.planned_start_datetime,
                      helperText: !editData.planned_start_datetime ? 'Planned start date & time is required' : '',
                      FormHelperTextProps: {
                        sx: { color: '#000000' }
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Planned Completion Date & Time *"
                  value={editData.planned_completion_datetime}
                  onChange={(newValue) => {
                    setEditData({ 
                      ...editData, 
                      planned_completion_datetime: newValue
                    });
                  }}
                  format={lineProductionTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                  ampm={lineProductionTimeFormat !== '24hour'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !editData.planned_completion_datetime,
                      helperText: !editData.planned_completion_datetime ? 'Planned completion date & time is required' : '',
                      FormHelperTextProps: {
                        sx: { color: '#000000' }
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 6: Production Details */}
            <Step>
              <StepLabel
                onClick={() => handleStep(5)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Production Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Line configuration, operational parameters, and inventory analysis
                </Typography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!editData.priority}>
                <InputLabel>Priority *</InputLabel>
                <Select
                  value={editData.priority || ''}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  label="Priority *"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
                {!editData.priority && (
                  <Typography variant="caption" sx={{ color: '#000000', mt: 0.5, fontSize: '0.75rem', ml: 2 }}>
                    Priority is required
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Production Line *"
                value={editData.production_line || ''}
                onChange={(e) => setEditData({ ...editData, production_line: e.target.value })}
                required
                error={!editData.production_line?.trim()}
                helperText={!editData.production_line?.trim() ? 'Production line is required' : ''}
                FormHelperTextProps={{
                  sx: { color: '#000000' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shift Supervisor *"
                value={editData.shift_supervisor || ''}
                onChange={(e) => setEditData({ ...editData, shift_supervisor: e.target.value })}
                required
                error={!editData.shift_supervisor?.trim()}
                helperText={!editData.shift_supervisor?.trim() ? 'Shift supervisor is required' : ''}
                FormHelperTextProps={{
                  sx: { color: '#000000' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Production Notes (Optional)"
                multiline
                rows={3}
                value={editData.production_notes || ''}
                onChange={(e) => setEditData({ ...editData, production_notes: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)'
                  }
                }}
              />
            </Grid>

            {/* Inventory Analysis */}
            {editData.production_location && editData.variant_distribution && editData.variant_distribution.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      <InventoryIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                        Component Availability Analysis
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Real-time inventory analysis for bottles, caps, labels and supplies
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  {inventoryAnalysis ? (
                    inventoryAnalysis.inventoryItems.length === 0 ? (
                      <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.06)}, ${alpha(theme.palette.info.main, 0.02)})`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.08)}`,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          p: 4,
                          textAlign: 'center'
                        }}>
                          <InventoryIcon sx={{ fontSize: 48, color: theme.palette.info.main, mb: 2 }} />
                          <Typography variant="h6" sx={{ mb: 1, color: theme.palette.info.main, fontWeight: 500 }}>
                            No Component Analysis Required
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This template doesn't require any bottles, caps, labels, or supplies.
                          </Typography>
                        </Box>
                      </Card>
                    ) : (
                    <Card sx={{ 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      }
                    }}>
                      <Box sx={{ 
                        p: 3, 
                        background: inventoryAnalysis.shortages.length === 0 ? 
                          `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})` : 
                          `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: inventoryAnalysis.shortages.length === 0 ? '#4caf50' : '#f44336',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}>
                            {inventoryAnalysis.shortages.length === 0 ? 
                              <InventoryIcon sx={{ fontSize: 28 }} /> : 
                              <InventoryIcon sx={{ fontSize: 28 }} />
                            }
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: '#000000',
                              mb: 0.5
                            }}>
                              {inventoryAnalysis.shortages.length === 0 ? 'All Components Available' : 'Component Shortages Detected'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}>
                              Real-time inventory analysis complete
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                LOCATION
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {getLocationLabel(editData.production_location)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                TARGET UNITS
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {(() => {
                                  if (!selectedUnitConfig || !editData.product_variant_id) return 0;
                                  return calculateTotalUnitsFromInputs(editData.product_variant_id, unitInputs[editData.product_variant_id]).toLocaleString();
                                })()} units
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                TEMPLATE
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {templates.find(t => t.id === editData.template_id)?.template_name || 'Not set'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 2, 
                              backgroundColor: 'rgba(255,255,255,0.8)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(0,0,0,0.1)',
                              height: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                backgroundColor: 'rgba(255,255,255,1)',
                                border: '1px solid rgba(0,0,0,0.2)'
                              }
                            }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                                CAN MAKE
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                {(inventoryAnalysis?.canMakeUnits || 0).toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Stack spacing={3} sx={{ p: 3 }}>
                        {/* Component Analysis Table */}
                        <Card sx={{ 
                          borderRadius: 2,
                          border: '1px solid #2196f3',
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                          }
                        }}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#e3f2fd',
                            borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <InventoryIcon sx={{ color: '#2196f3', fontSize: 24, mr: 1.5 }} />
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                Component-by-Component Analysis
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ p: 3 }}>
                            {inventoryAnalysis.inventoryItems.length > 0 ? (
                              <Grid container spacing={3}>
                                {inventoryAnalysis.inventoryItems.map((item, index) => {
                                  const isAvailable = item.canMake;
                                  
                                  return (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                      <Card sx={{ 
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        height: 200,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: `0 4px 16px ${alpha(theme.palette.grey[500], 0.12)}`,
                                          borderColor: isAvailable ? theme.palette.success.main : theme.palette.error.main
                                        }
                                      }}>
                                        {/* Status Header */}
                                        <Box sx={{ 
                                          backgroundColor: isAvailable ? '#e8f5e8' : '#ffebee',
                                          color: isAvailable ? theme.palette.success.main : theme.palette.error.main,
                                          p: 1.5,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderBottom: `1px solid ${isAvailable 
                                            ? alpha(theme.palette.success.main, 0.2)
                                            : alpha(theme.palette.error.main, 0.2)}`
                                        }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', textAlign: 'center' }}>
                                            {isAvailable ? 'AVAILABLE' : 'INSUFFICIENT'}
                                          </Typography>
                                        </Box>
                                        
                                        {/* Content */}
                                        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                          <Box>
                                            <Typography variant="h6" sx={{ 
                                              fontWeight: 500, 
                                              fontSize: '1rem', 
                                              mb: 1, 
                                              color: '#000000',
                                              lineHeight: 1.3,
                                              minHeight: '2.6em'
                                            }}>
                                              {item.type}
                                            </Typography>
                                          </Box>
                                          
                                          <Box>
                                            <Box sx={{ 
                                              display: 'flex', 
                                              justifyContent: 'center', 
                                              mt: 0,
                                              mb: 0,
                                              mx: 1,
                                              p: 1.5,
                                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                              borderRadius: 2,
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                                              boxShadow: `0 1px 4px ${alpha(theme.palette.grey[500], 0.1)}`
                                            }}>
                                              <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, display: 'block' }}>
                                                  NEED
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                  {item.needed.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                  units
                                                </Typography>
                                              </Box>
                                              <Box sx={{ textAlign: 'center', flex: 1, borderLeft: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, pl: 2 }}>
                                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, display: 'block' }}>
                                                  HAVE
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                  fontWeight: 500, 
                                                  color: isAvailable ? '#4caf50' : '#f44336'
                                                }}>
                                                  {item.available.toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                  units
                                                </Typography>
                                              </Box>
                                            </Box>
                                            
                                            {item.shortage > 0 && (
                                              <Typography variant="body2" sx={{ 
                                                textAlign: 'center', 
                                                color: '#f44336', 
                                                fontWeight: 500,
                                                fontStyle: 'italic',
                                                mt: 1
                                              }}>
                                                Order {item.shortage.toLocaleString()} more units
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No component analysis available.
                              </Typography>
                            )}
                          </Box>
                        </Card>

                        {/* Recommendation */}
                        <Card sx={{ 
                          borderRadius: 2,
                          border: '1px solid #4caf50',
                          backgroundColor: 'white',
                          boxShadow: '0 4px 16px rgba(76, 175, 80, 0.12)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                          }
                        }}>
                          <Box sx={{ 
                            p: 3, 
                            backgroundColor: '#e8f5e8',
                            borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <InventoryIcon sx={{ color: '#4caf50', fontSize: 28, mr: 1.5 }} />
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                Recommendation
                              </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ color: '#000000', fontWeight: 500, lineHeight: 1.6 }}>
                              {inventoryAnalysis.shortages.length === 0 ? 
                                'All components are available. Production can proceed as planned.' :
                                `Order the missing components above to complete production of ${inventoryAnalysis.totalUnitsNeeded} units.`
                              }
                            </Typography>
                          </Box>
                        </Card>
                      </Stack>
                    </Card>
                    )
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: alpha('#ff9800', 0.1), 
                      borderRadius: 2,
                      border: `1px solid ${alpha('#ff9800', 0.3)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading component analysis...
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </>
            )}
                </Grid>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleCancel} 
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep > 0 && (
              <Button 
                onClick={handleBack}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                Back
              </Button>
            )}
            
            {activeStep < steps.length - 1 ? (
              <Button 
                onClick={handleNext}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  }
                }}
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSave}
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving || !isFormValid()}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: !isFormValid() ? theme.palette.grey[400] : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: !isFormValid() ? theme.palette.grey[100] : 'white',
                  fontWeight: 500,
                  boxShadow: !isFormValid() ? 'none' : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: !isFormValid() ? theme.palette.grey[400] : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: !isFormValid() ? 'none' : `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  '&:disabled': {
                    background: theme.palette.grey[400],
                    color: theme.palette.grey[100]
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Line Production'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={productSelectionDialogOpen}
        onClose={handleCloseProductSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InventoryIcon sx={{ mr: 2 }} />
            Select Product
          </Box>
          <IconButton 
            onClick={handleCloseProductSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InventoryIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {!dialogSelectedProduct ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredProducts().map((product) => (
                    <TableRow 
                      key={product.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {product.sku || 'No SKU'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            setDialogSelectedProduct(product);
                            await fetchProductVariants(product.id);
                            // Auto-select product in editData for immediate feedback
                            setEditData(prev => ({
                              ...prev,
                              product_id: product.id,
                              product_variant_id: null,
                              product_variant: ''
                            }));
                          }}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedProduct?.id === product.id ? 'black' : 'transparent',
                            color: dialogSelectedProduct?.id === product.id ? 'white' : 'inherit',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              bgcolor: dialogSelectedProduct?.id === product.id ? 'black' : 'transparent',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredProducts().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {productSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ p: 3 }}>
                {/* Selected Product Display */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Selected Product:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {dialogSelectedProduct.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {dialogSelectedProduct.sku || 'No SKU'}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setDialogSelectedProduct(null);
                      setEditData(prev => ({
                        ...prev,
                        product_id: '',
                        product_variant_id: null,
                        product_variant: ''
                      }));
                    }}
                    sx={{ ml: 2, textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Change Product
                  </Button>
                </Box>

                {/* Variant Selection */}
                {productVariants && productVariants.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                      Select Variant:
                    </Typography>
                    {productVariants.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Variant</TableCell>
                            <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productVariants.map((variant) => (
                            <TableRow 
                              key={variant.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'grey.50',
                                  transform: 'translateX(4px)',
                                  boxShadow: 'inset 4px 0 0 black'
                                },
                                '& .MuiTableCell-root': {
                                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                  py: 2,
                                  pl: 3,
                                  pr: 3
                                }
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                    {variant.name || (variant.volume_weight ? `${variant.volume_weight}${variant.volume_unit}` : 'Variant')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    SKU: {variant.sku || 'No SKU'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={async () => {
                                    await handleVariantChange(variant.id);
                                    handleCloseProductSelection();
                                  }}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.8rem',
                                    px: 2,
                                    py: 1,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    bgcolor: editData.product_variant_id === variant.id ? 'black' : 'transparent',
                                    color: editData.product_variant_id === variant.id ? 'white' : 'inherit',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      bgcolor: editData.product_variant_id === variant.id ? 'black' : 'transparent',
                                      transform: 'translateY(-1px)',
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No variants available for this product.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                  }}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      ✓ This product has no variants. Ready to proceed.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => window.open(`/company/${userCompanyId}/products`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Product
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseProductSelection}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmProductSelection}
              variant="contained"
              disabled={!dialogSelectedProduct || (productVariants?.length > 0 && !editData.product_variant_id)}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              {productVariants?.length > 0 && !editData.product_variant_id ? 'Select Variant Above' : 'Confirm Selection'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Location Selection Dialog */}
      <Dialog
        open={locationSelectionDialogOpen}
        onClose={handleCloseLocationSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2 }} />
            Select Production Location
          </Box>
          <IconButton 
            onClick={handleCloseLocationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations by name, facility type, city, or state..."
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredLocations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {locationSearchTerm ? 'No locations found matching your search.' : 'No production locations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLocations().map((location) => (
                    <TableRow
                      key={location.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectLocation(location)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {location.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectLocation(location);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseLocationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => window.open(`/company/${userCompanyId}/locations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog
        open={templateSelectionDialogOpen}
        onClose={handleCloseTemplateSelection}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '100vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Production Template
          </Typography>
          <IconButton onClick={handleCloseTemplateSelection} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={templateSearchTerm}
              onChange={(e) => setTemplateSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PrecisionManufacturingIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredTemplates().map((template) => (
                  <TableRow 
                    key={template.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {template.template_name}
                        </Typography>
                        {template.product?.name && (
                          <Typography variant="caption" color="text.secondary">
                            Product: {template.product.name}
                          </Typography>
                        )}
                        {template.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {template.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTemplateSelect(template)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: 'white',
                          color: 'text.primary',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredTemplates().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {templateSearchTerm ? 'No templates found matching your search.' : 'No templates available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => {
              window.open(`/company/${userCompanyId}/manufacturing/production-line/templates`, '_blank');
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Template
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseTemplateSelection}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Batch Selection Dialog */}
      <Dialog
        open={batchSelectionDialogOpen}
        onClose={handleCloseBatchSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalDrinkIcon sx={{ mr: 2 }} />
            Select Batch Production to Link
          </Box>
          <IconButton 
            onClick={handleCloseBatchSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search batches by batch number, lot number, or product..."
              value={batchSearchTerm}
              onChange={(e) => setBatchSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalDrinkIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Batch Info
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Product & Status
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Cost Information
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredBatches().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {batchSearchTerm ? 'No batches found matching your search.' : 'No production batches available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredBatches().map((batch) => (
                    <TableRow
                      key={batch.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleBatchSelect(batch)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {batch.batch_number}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                            {batch.product?.name ? `${batch.product.name}${batch.product_variant ? ` (${batch.product_variant})` : ''}` : 'No product'}
                          </Typography>
                          
                          {/* Show batch quantity */}
                          {batch.planned_quantity && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {batch.planned_quantity} {batch.planned_unit_of_measure || 'units'}
                            </Typography>
                          )}
                          
                          <Chip 
                            label={batch.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Draft'}
                            size="small"
                            sx={{ 
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              mt: 0.5
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {(() => {
                            const batchCosts = batch.batch_costs && batch.batch_costs.length > 0 ? batch.batch_costs[0] : null;
                            const totalCost = batchCosts ? (batchCosts.total_production_batch_cost || batchCosts.total_cost) : (batch.actual_material_cost || batch.planned_material_cost || 0);
                            // Always recalculate cost per unit correctly
                            const totalUnits = batch.units_to_make || 0;
                            const costPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
                            
                            return (
                              <>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                  ${totalCost.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} {batchCosts ? '(Full)' : '(Est)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ${costPerUnit.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}/${batch.planned_unit_of_measure || 'unit'}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBatchSelect(batch);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseBatchSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setEditData({ ...editData, batch_id: '', batch_total_material_cost: 0, batch_cost_per_unit: 0 });
              setSelectedBatch(null);
              handleCloseBatchSelection();
            }}
            variant="outlined"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Clear Selection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Configuration Selection Dialog */}
      <Dialog
        open={unitConfigSelectionDialogOpen}
        onClose={handleCloseUnitConfigSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '100vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Unit Configuration
          </Typography>
          <IconButton onClick={handleCloseUnitConfigSelection} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search unit configurations..."
              value={unitConfigSearchTerm}
              onChange={(e) => setUnitConfigSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {getFilteredUnitConfigurations().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {unitConfigSearchTerm ? 'No unit configurations found matching your search.' : 'No unit configurations available.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Configuration</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }}>Structure</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Details</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredUnitConfigurations().map((config) => {
                    const isSelected = selectedUnitConfig?.id === config.id;
                    const getTotalUnitsPerPallet = (config) => {
                      if (config.enable_pallets && config.enable_cases) {
                        return config.units_per_case * config.cases_per_pallet;
                      }
                      return null;
                    };

                    return (
                      <TableRow 
                        key={config.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                          '&:hover': {
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            pl: 3,
                            pr: 3
                          }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {config.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              {config.enable_cases && (
                                <Chip 
                                  label="Cases" 
                                  size="small" 
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    color: 'info.main',
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 20
                                  }}
                                />
                              )}
                              {config.enable_pallets && (
                                <Chip 
                                  label="Pallets" 
                                  size="small" 
                                  sx={{
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: 'success.main',
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 20
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            {config.enable_cases && !config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name}`}
                            {config.enable_cases && config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`}
                            {!config.enable_cases && !config.enable_pallets && `${config.base_unit_name} only`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              Units/Case: {config.enable_cases ? config.units_per_case : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              Cases/Pallet: {config.enable_pallets ? config.cases_per_pallet : 'N/A'}
                            </Typography>
                            {getTotalUnitsPerPallet(config) && (
                              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, display: 'block', mt: 0.5 }}>
                                Total: {getTotalUnitsPerPallet(config).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectUnitConfig(config)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 1,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              bgcolor: isSelected ? 'black' : 'transparent',
                              color: isSelected ? 'white' : 'inherit',
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                border: `1px solid ${theme.palette.primary.main}`,
                                bgcolor: isSelected ? 'black' : 'transparent',
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            }}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => {
              handleCloseUnitConfigSelection();
              setUnitConfigDialogOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Configuration
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseUnitConfigSelection}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Unit Configuration Creation/Edit Dialog */}
      <Dialog
        open={unitConfigDialogOpen}
        onClose={handleCloseUnitConfigDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {editingUnitConfig ? 'Edit Configuration' : 'Add Configuration'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3,
                mt: 4
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <SettingsIcon sx={{ fontSize: 28 }} />
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Configuration Name"
                      value={unitConfigFormData.name}
                      onChange={(e) => setUnitConfigFormData({...unitConfigFormData, name: e.target.value})}
                      placeholder="e.g., Standard Bottles, Large Cases, Individual Units"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Base Unit Name"
                      value={unitConfigFormData.base_unit_name}
                      onChange={(e) => setUnitConfigFormData({...unitConfigFormData, base_unit_name: e.target.value})}
                      placeholder="e.g., units, bottles, pieces"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <InventoryIcon sx={{ fontSize: 28 }} />
                  Packaging Structure
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_cases}
                          onChange={(e) => setUnitConfigFormData({
                            ...unitConfigFormData, 
                            enable_cases: e.target.checked,
                            enable_pallets: e.target.checked ? unitConfigFormData.enable_pallets : false
                          })}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Cases
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group individual units into cases for easier handling
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Case Unit Name"
                          value={unitConfigFormData.case_unit_name}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, case_unit_name: e.target.value})}
                          placeholder="e.g., cases, boxes, packs"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Units per Case"
                          type="number"
                          value={unitConfigFormData.units_per_case}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, units_per_case: e.target.value})}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many individual units make up one case"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_pallets}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, enable_pallets: e.target.checked})}
                          color="primary"
                          disabled={!unitConfigFormData.enable_cases}
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Pallets
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group cases into pallets for bulk storage {!unitConfigFormData.enable_cases && '(requires cases to be enabled)'}
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_pallets && unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Pallet Unit Name"
                          value={unitConfigFormData.pallet_unit_name}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, pallet_unit_name: e.target.value})}
                          placeholder="e.g., pallets, skids"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Cases per Pallet"
                          type="number"
                          value={unitConfigFormData.cases_per_pallet}
                          onChange={(e) => setUnitConfigFormData({...unitConfigFormData, cases_per_pallet: e.target.value})}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many cases fit on one pallet"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    Quick Presets
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: false, enable_pallets: false})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Units Only
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: false, units_per_case: 12})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Cases (12 units)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: true, units_per_case: 12, cases_per_pallet: 42})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Full (12×42)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData({...unitConfigFormData, enable_cases: true, enable_pallets: true, units_per_case: 24, cases_per_pallet: 48})}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Large (24×48)
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={handleCloseUnitConfigDialog} 
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUnitConfigSubmit}
            disabled={savingUnitConfig || !unitConfigFormData.name}
            startIcon={savingUnitConfig ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {savingUnitConfig ? 'Saving...' : (editingUnitConfig ? 'Update Configuration' : 'Create Configuration')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ProductionLineOverview;